import React, { useEffect, useState, useRef, useCallback } from 'react';
import styled from 'styled-components';
import Breadcrumbs from '../../components/Breadcrumbs';
import '../../styles/PacDebt.css';
import Footer from '../../components/Footer';

// ─── API Config ───────────────────────────────────────────────────────────────

const MAIN_API_URL =
  process.env.REACT_APP_SETTLEMENT_QUEUE_API_URL ||
  'https://oph-settlements-wf-prod.sizzard.workers.dev'; // GET ?page=1&per_page=100

const CLAIM_API = 'https://oph-claims-wf-prod.sizzard.workers.dev'; // POST { debt_id: xxx, action: 'claim'|'release'|'block', reason?: '...' }
const NOTES_API = 'https://oph-claims-wf-prod.sizzard.workers.dev'; // GET ?debt_id=xxx&action=get_notes
const ACTIVITY_LOG_API = 'https://eoay1adj9mu05me.m.pipedream.net'; // dedicated cycle-time history endpoint


const getLoggedInUser = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user')) || {};
    return { name: [u.firstName, u.lastName].filter(Boolean).join(' ') || 'Unknown', ...u };
  } catch { return { name: 'Unknown' }; }
};

// Recognizes notes that the workflow wrote on behalf of an action (claim,
// release, block) — rendered differently in the NoteModal so they don't
// blend in with operator commentary. Tolerant of a few likely tag shapes
// in case the workflow author picks a different label.
const isSystemNote = (by) => {
  if (!by) return false;
  const s = String(by).trim().toLowerCase();
  return s === '[system]' || s === 'system' || s === '(system)';
};

// A "status" entry is the lifecycle line the workflow writes (Claimed, In
// Progress, Released) as opposed to free-text operator commentary. The API
// distinguishes the two by `type` ("Posted Status" vs "Posted Note") when it's
// present; the live get_notes response currently omits `type` and instead tags
// status lines with by="[System]" (see isSystemNote). Honor either shape so the
// derivation is correct against both. In-progress state is driven ONLY by
// status entries, so an operator note added afterward never clears it.
const isStatusEntry = (entry) => {
  if (!entry) return false;
  const type = entry.type;
  if (type != null && String(type).trim() !== '') {
    return String(type).trim().toLowerCase() === 'posted status';
  }
  return isSystemNote(entry.noted_by ?? entry.by);
};

// The MOST RECENT status entry in a row's note history — the line that decides
// the Process button's state. `entries` is the per-row history from
// get_state/get_notes, assumed chronological (oldest first). Returns the raw
// entry, or null when the row has no status entries at all.
const latestStatusEntry = (entries) => {
  if (!Array.isArray(entries) || !entries.length) return null;
  const statusEntries = entries.filter(isStatusEntry);
  return statusEntries.length ? statusEntries[statusEntries.length - 1] : null;
};

const entryText = (entry) => String(entry?.note ?? entry?.text ?? '').trim();
const entryAt   = (entry) => entry?.timestamp ?? entry?.at ?? null;

// On reload the local `inProgressBy` marker is gone (session state is cleared
// on refresh), so we re-derive it from the row's note history: the row is in
// progress iff its MOST RECENT status entry reads "In Progress by <user>". Any
// later status entry (a Release, another claim, etc.) supersedes it, so a
// released row reads as not-in-progress; a plain operator note written after
// leaves the marker intact. Returns { by, at } for the marker, or null when the
// last status entry isn't an "In Progress by" line.
const IN_PROGRESS_EVENT_RE = /in[\s-]*progress\s+by\s+(.+)$/i;
const deriveInProgressFromLatestNote = (entries) => {
  const last = latestStatusEntry(entries);
  if (!last) return null;
  const m = entryText(last).match(IN_PROGRESS_EVENT_RE);
  if (!m) return null;
  return { by: m[1].trim(), at: entryAt(last) };
};

// True when the MOST RECENT status entry is an explicit "Released by <user>".
// A release hard-resets the Process button back to "Process" (enabled) — it
// overrides even a local optimistic in-progress marker, since the server has
// recorded that the row was let go.
const RELEASED_EVENT_RE = /released\s+by\b/i;
const isLatestStatusReleased = (entries) =>
  RELEASED_EVENT_RE.test(entryText(latestStatusEntry(entries)));

// Label for the Note action button. Splits the count between manual
// operator notes and system events (auto-notes from claim/release/block
// actions) so users can tell "is there commentary on this row, or just
// activity?" at a glance, without opening the modal.
//   - No history             → "Note"
//   - Only manual notes      → "Note (3)"
//   - Only events            → "Events (5)"
//   - Both                   → "Note (3) · Events (5)"
// Server-side counts come from get_state (noteCounts / eventCounts);
// local in-session entries are also bucketed by isSystemNote(by) so a
// just-added operator note shows up immediately without waiting for refresh.
const noteButtonLabel = (record) => {
  const localNotes  = record.notes ?? [];
  const localManual = localNotes.filter((n) => !isSystemNote(n.by)).length;
  const localEvents = localNotes.filter((n) =>  isSystemNote(n.by)).length;
  const noteCount   = (record.serverNoteCount  ?? 0) + localManual;
  const eventCount  = (record.serverEventCount ?? 0) + localEvents;
  if (!noteCount && !eventCount) return 'Note';
  const parts = [];
  if (noteCount  > 0) parts.push(`Note (${noteCount})`);
  if (eventCount > 0) parts.push(`Events (${eventCount})`);
  return parts.join(' · ');
};

// ─── API Normalizer ───────────────────────────────────────────────────────────

const SPECIAL_STATUSES = new Set(['Missing Letter Pays Submitted', 'Processing Issue']);

// Records whose processing is fully done — these are excluded from the active
// dashboard partition entirely (no longer surfaced in Main/Special).
const COMPLETED_STATUSES = new Set(['Completed']);

// "Cycle clock has stopped." A settlement's processing-time clock ends when the
// CRM moves it to 'Completed' OR 'Missing Letter Pays Submitted'. Both feed the
// Activity Log. We do NOT track time from 'Missing Letter Pays Submitted' to a
// later 'Completed' transition — that's a separate downstream phase.
//
// A Missing Letter record therefore appears in BOTH the Special Populations tab
// (it's still active work) AND the Activity Log (its cycle clock has stopped).
const ACTIVITY_LOG_STATUSES = new Set(['Completed', 'Missing Letter Pays Submitted']);

// API → internal mapping.
// User-visible terms (filter labels, status badges, column headers) are preserved;
// only field names and value shapes change to match the Pipedream endpoint.
// Keys are trimmed before lookup — the CRM occasionally emits trailing whitespace
// (e.g. "Processing Issue ") that would otherwise miss the partition.
const OFFER_STATUS_MAP = {
  'Ready For Processing':            'Ready Full',
  'Ready For Partial Processing':    'Ready Partial',
  'Missing Letter (Pays Submitted)': 'Missing Letter Pays Submitted',
  'Processing Issue':                'Processing Issue',
};

// All age math anchors to Pacific Time (America/Los_Angeles), regardless of
// where the user's browser is running. This is the canonical reference the
// settlements team operates in.
const TIME_ZONE = 'America/Los_Angeles';

// API timestamps come in as `2026-02-28T10:37:39.000Z` but the underlying
// values are Pacific clock time, not UTC (verified against the API's own
// AGE_DURATION). Treat the components as Pacific local and return the actual
// UTC instant they represent — handles PST/PDT transitions automatically.
const parseAsPacific = (isoString) => {
  if (!isoString) return null;
  const stripped = String(isoString).replace(/Z$/, '');
  const m = stripped.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
  if (!m) return null;
  const [, y, mo, d, h, mi, s] = m;
  // Step 1: pretend the components are UTC, get a reference instant.
  const asIfUtc = Date.UTC(+y, +mo - 1, +d, +h, +mi, +s);
  // Step 2: see what Pacific clock reads at that instant — the difference is
  // the Pacific offset to apply.
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
  const parts = fmt.formatToParts(new Date(asIfUtc)).reduce((acc, p) => {
    if (p.type !== 'literal') acc[p.type] = p.value;
    return acc;
  }, {});
  const pacificAsIfUtc = Date.UTC(+parts.year, +parts.month - 1, +parts.day,
                                  +parts.hour, +parts.minute, +parts.second);
  return new Date(asIfUtc + (asIfUtc - pacificAsIfUtc));
};

// Calendar-day difference in Pacific from today to the given API date string
// ("2025-10-21T00:00:00.000Z" or "2025-10-21"). Returns:
//   positive  → future (days until due)
//   0         → due today
//   negative  → overdue (days past due)
//   null      → date missing or unparseable
// Uses Pacific calendar so a payment due "Oct 21" reads consistently regardless
// of the user's browser timezone.
const daysUntilInPacific = (apiDateString) => {
  if (!apiDateString) return null;
  const m = String(apiDateString).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const [, y, mo, d] = m;
  const targetUtc = Date.UTC(+y, +mo - 1, +d);
  const today = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE,
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
  const [ty, tm, td] = today.split('-').map(Number);
  return Math.round((targetUtc - Date.UTC(ty, tm - 1, td)) / 86_400_000);
};

// Calendar-day midnight crossings in Pacific between two timestamps. This
// matches how the API computes its day count (Feb 28 → May 7 = 68 days,
// regardless of time of day) — even though the actual elapsed fractional days
// is 67.97. Frontend's days bucket uses this so displays line up with the API.
const pacificCalendarDayDiff = (a, b) => {
  if (!a || !b) return null;
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE,
    year: 'numeric', month: '2-digit', day: '2-digit',
  });
  const [ay, am, ad] = fmt.format(a).split('-').map(Number);
  const [by, bm, bd] = fmt.format(b).split('-').map(Number);
  return Math.round(
    (Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86_400_000
  );
};

// Pacific year-month bucket: returns YYYYMM as an integer (e.g. 202606 for
// June 2026). Accepts two input shapes:
//   - "YYYY-MM-DD..." string — uses the date portion AS-IS (the API stamps
//     pure dates as midnight-Z but the Z is misleading; the value already
//     IS the Pacific calendar day, so no shifting needed)
//   - Date object — converts via Intl.DateTimeFormat with Pacific timezone
// Passing a Date built from "2026-06-23T00:00:00.000Z" would otherwise
// return 202605 (because midnight UTC = 5pm PDT prev day) — the string
// path avoids that off-by-one.
const pacificYearMonth = (input) => {
  if (!input || input === '—') return null;
  if (typeof input === 'string') {
    const m = input.slice(0, 10).match(/^(\d{4})-(\d{2})-\d{2}$/);
    return m ? Number(m[1]) * 100 + Number(m[2]) : null;
  }
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return null;
  const [y, m] = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE,
    year: 'numeric', month: '2-digit',
  }).format(d).split('-').map(Number);
  return y * 100 + m;
};

const currentPacificYearMonth = () => pacificYearMonth(new Date());

const previousPacificYearMonth = () => {
  const ym = currentPacificYearMonth();
  const y = Math.floor(ym / 100), m = ym % 100;
  return m === 1 ? (y - 1) * 100 + 12 : y * 100 + (m - 1);
};

// Pacific year-month-day as an integer YYYYMMDD (e.g. 20260609 for
// 2026-06-09). Same dual-input behavior as pacificYearMonth: string
// inputs use the date portion as-is; Date inputs convert via Pacific
// timezone formatting.
const pacificYMD = (input) => {
  if (!input || input === '—') return null;
  if (typeof input === 'string') {
    const m = input.slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? Number(m[1] + m[2] + m[3]) : null;
  }
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return null;
  const [y, m, dd] = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE,
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(d).split('-').map(Number);
  return y * 10000 + m * 100 + dd;
};

// Date Range filter modes. Used by all three tabs.
const DATE_RANGE_MODES = [
  { id: 'all',      label: 'All' },
  { id: 'current',  label: 'Current Month' },
  { id: 'previous', label: 'Previous Month' },
  { id: 'custom',   label: 'Custom' },
];

// Date-field registries — the columns a user can filter "by". Each entry's
// `get(record)` returns the canonical YYYY-MM-DD string (or '—'/null when
// missing). String form, not Date, so the filter and the displayed column
// stay in sync (see the off-by-one fix for the rationale).
const MAIN_DATE_FIELDS = [
  { id: 'firstOfferPaymentDate', label: 'First Offer Payment Date', get: (r) => r.firstOfferPaymentDate },
  { id: 'submittedDate',         label: 'Submitted For Processing', get: (r) => r.submittedDate },
  { id: 'statusDate',            label: 'Status Date',              get: (r) => r.statusDate },
];

// Activity Log has just one date column — the dropdown collapses to a
// no-op when fieldOptions has a single entry (see DateRangeToggle).
const ACTIVITY_DATE_FIELDS = [
  { id: 'settlementDate', label: 'Settlement Date', get: (r) => r.settlementDate },
];

// Predicate factory: returns a fn that, given a Date (or null), reports
// whether it falls inside the selected window.
//   'all'              → accepts everything, including null/missing dates
//   'current'/'previous' → reject null dates; match Pacific calendar month
//   'custom'           → inclusive From/To window (either bound may be null
//                        to mean open-ended); rejects null dates
// customFrom / customTo are 'YYYY-MM-DD' strings from <input type="date">.
const buildDateRangePredicate = (mode, customFrom, customTo) => {
  if (mode === 'all' || !mode) return () => true;
  if (mode === 'custom') {
    // No bounds set → behaves as 'all' (so freshly-selected Custom with
    // empty inputs doesn't blank the table while the user is picking).
    if (!customFrom && !customTo) return () => true;
    const fromN = customFrom ? Number(customFrom.replace(/-/g, '')) : null;
    const toN   = customTo   ? Number(customTo.replace(/-/g, ''))   : null;
    return (date) => {
      const ymd = pacificYMD(date);
      if (ymd == null) return false;
      if (fromN != null && ymd < fromN) return false;
      if (toN   != null && ymd > toN)   return false;
      return true;
    };
  }
  const cur  = currentPacificYearMonth();
  const prev = previousPacificYearMonth();
  const target = mode === 'current' ? cur : prev;
  return (date) => {
    const ym = pacificYearMonth(date);
    return ym === target;
  };
};

// Age = now - SUBMITTED_FOR_PROCESSING. Display rules:
//   < 1 hour       → "45m"
//   1 hour to <7h  → "1h", "2h", … "6h"  (full 6th hour displays as "6h")
//   7 hours and up → "1 day", "2 days", "3 days" (calendar-day counting in Pacific)
// Returns { label, hours } so callers can also sort/filter by numeric hours.
const formatAgeFromSubmitted = (submittedAt) => {
  if (!submittedAt) return { label: null, hours: null };
  const now = new Date();
  const ms = now.getTime() - submittedAt.getTime();
  if (!Number.isFinite(ms) || ms < 0) return { label: null, hours: null };
  const minutes = ms / 60_000;
  const hours   = ms / 3_600_000;
  let label;
  if (hours < 1) {
    label = `${Math.floor(minutes)}m`;
  } else if (hours < 7) {
    // Floor so 5.9h still reads as "5h" — and 6.0h–6.99h reads as "6h" for
    // the entire 6th hour rather than vanishing into "1 day" at 6.0001h.
    label = `${Math.floor(hours)}h`;
  } else {
    // Calendar-day count in Pacific (matches the API's day arithmetic).
    // Falls back to floor-of-hours if the calendar diff somehow comes back
    // as 0 — defensive for clock skew at the 7h boundary.
    const calendarDays = pacificCalendarDayDiff(submittedAt, now);
    const days = Math.max(1, calendarDays || Math.floor(hours / 24));
    label = `${days} day${days > 1 ? 's' : ''}`;
  }
  return { label, hours };
};

const normalizeApiRecord = (r) => {
  // SUBMITTED_FOR_PROCESSING arrives stamped with Z but the value is actually
  // Pacific clock time. parseAsPacific produces the correct absolute instant
  // regardless of where the user's browser is running.
  const submittedAt = parseAsPacific(r.SUBMITTED_FOR_PROCESSING);
  const age = formatAgeFromSubmitted(submittedAt);
  const offerStatus = (() => {
    const raw = (r.OFFER_STATUS || '').trim();
    return OFFER_STATUS_MAP[raw] || raw;
  })();
  // For terminal-state records the CRM's status-change date IS the moment the
  // cycle clock stopped. When the API later exposes a dedicated COMPLETED_DATE,
  // prefer that — left as a TODO until the workflow ships it.
  const isCycleComplete = ACTIVITY_LOG_STATUSES.has(offerStatus);
  const completedAt = isCycleComplete && r.SO_OFFER_STATUS_DATE
    ? new Date(r.SO_OFFER_STATUS_DATE)
    : null;
  return {
    id:          r.SO_ID,
    offerId:     r.SO_ID,
    creditorId:  null,
    addedAt:     r.SO_OFFER_STATUS_DATE ? new Date(r.SO_OFFER_STATUS_DATE) : null,
    // Age is computed client-side as (now - SUBMITTED_FOR_PROCESSING) — see
    // formatAgeFromSubmitted. Recomputed on every refresh, so the label stays
    // current without server cooperation.
    ageLabel:    age.label,
    ageHours:    age.hours,
    // Days until first payment is due, in Pacific calendar days. Negative for
    // overdue records, 0 for due-today, positive for future. Used for the
    // "First Payment Due ≤ 48 hrs" filter and the AgeBadge urgency styling.
    // Replaces the API's LESS_THAN_48H flag (which uses server-side timezone
    // math we don't control).
    daysUntilFirstPayment: daysUntilInPacific(r.FIRST_OFFER_PAYMENT_DATE),
    // "Within 48 hrs" = the payment moment is within ~48 hours of today, in
    // either direction. Calendar-day window of ±2 in Pacific:
    //   -2 = overdue by 2 days   (still "just slipped")
    //   -1 = overdue by 1 day
    //    0 = due today
    //    1 = due tomorrow
    //    2 = due day after tomorrow
    // Anything older than 2 days past due is stale, not "urgent now"; anything
    // more than 2 days out is not yet imminent.
    dueWithin48hrs: (() => {
      const d = daysUntilInPacific(r.FIRST_OFFER_PAYMENT_DATE);
      return d != null && d >= -2 && d <= 2;
    })(),
    offerStatus,
    // Display matches what the API emits — slice the date portion off the ISO
    // timestamp ("2023-04-25T00:00:00.000Z" → "2023-04-25") rather than locale-
    // converting (which would render "4/25/2023" and obscure the source).
    firstOfferPaymentDate:    r.FIRST_OFFER_PAYMENT_DATE ? String(r.FIRST_OFFER_PAYMENT_DATE).slice(0, 10) : '—',
    firstOfferPaymentDateRaw: r.FIRST_OFFER_PAYMENT_DATE ? new Date(r.FIRST_OFFER_PAYMENT_DATE)            : null,
    // String date forms exposed for the Date Range filter's "filter by"
    // dropdown. Slicing the date portion gives the Pacific calendar day
    // (per the API's convention of stamping with Z but emitting Pacific
    // clock components — see parseAsPacific note).
    submittedDate:            r.SUBMITTED_FOR_PROCESSING ? String(r.SUBMITTED_FOR_PROCESSING).slice(0, 10) : '—',
    statusDate:               r.SO_OFFER_STATUS_DATE     ? String(r.SO_OFFER_STATUS_DATE).slice(0, 10)     : '—',
    clientId:         String(r.SO_CONTACT_ID ?? ''),
    clientName:       r.C_FULLNAME || null,
    // Current and Original are distinct CRM fields — never substitute one
    // for the other. If only one side is populated, the other renders blank
    // so the gap is visible to whoever is reading the table.
    currentCreditor:  r.CURRENT_CREDITOR  || '',
    originalCreditor: r.ORIGINAL_CREDITOR || '',
    debtId:           String(r.SO_DEBT_ID ?? ''),
    epfTotal:         parseCurrency(r.SO_SETTLEMENT_FEE),
    // CURRENT_MONTH_EPF_SUM arrives as a formatted string (e.g. "  $611.00").
    // parseCurrency tolerates both that and raw numbers; the column display,
    // the "> $1,000" filter, and the client-side sum fallback all need a real
    // number, not a string that Number() coerces to NaN.
    epfCurrentMo:     parseCurrency(r.CURRENT_MONTH_EPF_SUM),
    offerPaymentMethod: r.OFFER_PAYMENT_METHOD === '—' ? '' : (r.OFFER_PAYMENT_METHOD || ''),
    // Gateway comes from the GATEWAY_NAME field on the Pipedream workflow
    // at eo50wwbfxqdmwof. Treat '—' the same as null so the cell renders
    // the neutral em-dash placeholder via the column's render path.
    gateway:     r.GATEWAY_NAME && r.GATEWAY_NAME !== '—' ? r.GATEWAY_NAME : null,
    negotiator:  r.NEGOTIATOR || r.ASSIGNED_TO_NAME || '—',
    assignedTo:  r.ASSIGNED_TO || null,
    claimedBy:   r.CLAIMED_BY || null,
    // SIF_AUTH / SIF_LETTER come back as status strings from the CRM
    // (e.g. "received", "pending", "physical rcvd"). Pass through raw so
    // BoolCell renders the label; null still shows as ✗.
    sifAuth:     r.SIF_AUTH   || null,
    sifLetter:   r.SIF_LETTER || null,
    // SUMMON_PRE_LEGAL is a combined CRM flag ("Yes" or null) — mirrored into both
    // sub-flags so the combined "Summons / Pre-Legal" filter works regardless of
    // which one the record actually represents. HAS_SUMMONS is honored too if the
    // API ever emits it again.
    summons:  r.SUMMON_PRE_LEGAL === 'Yes' || r.HAS_SUMMONS === '1' || r.HAS_SUMMONS === 1 || r.HAS_SUMMONS === true,
    preLegal: r.SUMMON_PRE_LEGAL === 'Yes',
    blockReason: null,
    // READY_DATE isn't emitted by the endpoint yet, so cycle-time math will
    // show "—" until the workflow adds it. completedAt is derived above from
    // the status-change date for terminal-state rows.
    readyAt:         null,
    completedAt,
    completedStatus: isCycleComplete ? offerStatus : null,
    fetchError:      r.FETCH_ERROR || null,
  };
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Display amounts with exactly two decimal places. Truncate (don't round) so
// the displayed value never inflates past what the API gave us — e.g. 295.75
// shows as "$295.75", 367.5 as "$367.50", 296 as "$296.00".
const fmt = (n) => {
  if (n == null) return '—';
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  const truncated = Math.trunc(num * 100) / 100;
  return '$' + truncated.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const ROWS_PER_PAGE = 100;
// Tightened from 3 min → 60 s to shrink the window where two users can act on
// the same row without seeing each other's claims/blocks. Combined with the
// tab-focus refresh below, the practical staleness window drops to seconds.
const REFRESH_INTERVAL_MS = 60 * 1000; // 1 minute

// ─── CSV Export ───────────────────────────────────────────────────────────────

// Interactive / bookkeeping columns are never exported — they carry buttons and
// per-user claim state, not data anyone needs in a spreadsheet.
const EXPORT_EXCLUDED_COLUMNS = new Set(['actions', 'claimedBy', 'notes']);

// Display placeholders ("—") export as empty cells so spreadsheet filters and
// formulas see a blank rather than a literal dash.
const csvText = (v) => (v == null || v === '' || v === '—' ? '' : String(v));

// SIF Auth / SIF Letter arrive as booleans OR as CRM status strings
// ("received", "pending", ...). Mirror BoolCell's three-way read in text form.
const csvBool = (v) => {
  if (v == null || v === '') return '';
  if (v === true)  return 'Yes';
  if (v === false) return 'No';
  return String(v);
};

// Currency columns export as bare numbers (no "$", no thousands separators) so
// Excel/Sheets treat them as numeric and can sum them. fmt()'s truncation is
// preserved so the exported figure never exceeds the displayed one.
const csvAmount = (n) => {
  if (n == null) return '';
  const num = Number(n);
  if (!Number.isFinite(num)) return '';
  return (Math.trunc(num * 100) / 100).toFixed(2);
};

// Filename date stamp. Uses the Pacific calendar day so the name matches the
// dates inside the file (the API emits Pacific days — see parseAsPacific).
const csvDateStamp = () => new Intl.DateTimeFormat('en-CA', {
  timeZone: TIME_ZONE,
  year: 'numeric', month: '2-digit', day: '2-digit',
}).format(new Date());

const csvCell = (value) => {
  const s = value == null ? '' : String(value);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// Exports the rows the user is currently looking at (every page of the active
// filter + sort, not just the visible page) using the on-screen column order,
// minus EXPORT_EXCLUDED_COLUMNS. `columns` is the tab's ordered column list —
// each entry supplies an `exportValue(row)` extractor.
const downloadCsv = (columns, rows, filenamePrefix) => {
  const cols = columns.filter((c) => !EXPORT_EXCLUDED_COLUMNS.has(c.key) && c.exportValue);
  const lines = [cols.map((c) => csvCell(c.label)).join(',')];
  rows.forEach((row) => {
    lines.push(cols.map((c) => csvCell(c.exportValue(row))).join(','));
  });
  // Leading BOM so Excel opens the file as UTF-8 instead of mangling accents.
  const blob = new Blob(['\ufeff' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `${filenamePrefix}_${csvDateStamp()}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ─── Sub-components ───────────────────────────────────────────────────────────

// Renders the precomputed ageLabel (from SUBMITTED_FOR_PROCESSING). When the
// API didn't provide that timestamp, ageLabel is null and we show an em-dash —
// no fallback computation from other date fields.
const AgeBadge = ({ ageLabel, dueWithin48hrs }) => {
  if (!ageLabel) return <span style={{ color: '#adb5bd' }}>—</span>;
  return (
    <span style={{
      background: dueWithin48hrs ? '#f8d7da' : '#fff3cd',
      color:      dueWithin48hrs ? '#721c24' : '#856404',
      padding: '4px 10px',
      borderRadius: '6px',
      fontWeight: 600,
      fontSize: '11px',
      whiteSpace: 'nowrap',
    }}>{ageLabel}</span>
  );
};

const StatusBadge = ({ status }) => {
  const styles = {
    // Both Ready states are "good to process" but visually distinct so a
    // glance at the column tells you full vs. partial:
    //   Ready Full    → green   (complete balance offer)
    //   Ready Partial → orange  (partial-payment offer)
    'Ready Full':            { background: '#d4edda', color: '#155724' },
    'Ready Partial':         { background: '#ffedd5', color: '#9a3412' },
    'Ready For Processing':  { background: '#d4edda', color: '#155724' },
    'In Progress':           { background: '#cce5ff', color: '#004085' },
    'Missing Letter Pays Submitted': { background: '#fff3cd', color: '#856404' },
    'Processing Issue':      { background: '#f8d7da', color: '#721c24' },
  };
  const s = styles[status] || { background: '#e9ecef', color: '#495057' };
  return (
    <span style={{
      ...s,
      padding: '4px 10px',
      borderRadius: '6px',
      fontWeight: 600,
      fontSize: '11px',
      textTransform: 'uppercase',
      letterSpacing: '0.3px',
      whiteSpace: 'nowrap',
    }}>{status}</span>
  );
};

const ClientIdCell = ({ id }) => {
  const [copied, setCopied] = useState(false);
  const handleClick = () => {
    navigator.clipboard.writeText(id).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 300);
  };
  return (
    <span
      onClick={handleClick}
      title="Click to copy"
      style={{
        fontFamily: "'Courier New', monospace",
        background: copied ? '#a5cc2d' : '#f8f9fa',
        padding: '3px 8px',
        borderRadius: '4px',
        fontSize: '11px',
        color: '#495057',
        cursor: 'copy',
        display: 'inline-block',
        transition: 'background 0.2s',
      }}
    >{id}</span>
  );
};

const BoolCell = ({ val }) => {
  // Missing values render as a neutral em-dash, not a red ✗. ✗ is reserved for
  // an explicit `false` ("no, this was rejected"); null means "no data yet".
  if (val == null || val === '') return <span style={{ color: '#adb5bd' }}>—</span>;
  if (val === true)  return <span style={{ color: '#28a745', fontWeight: 600 }}>✓</span>;
  if (val === false) return <span style={{ color: '#dc3545', fontWeight: 600 }}>✗</span>;
  // Status string from CRM (e.g. "received", "pending", "physical rcvd")
  return <span style={{ color: '#28a745', fontWeight: 600, fontSize: '11px', textTransform: 'capitalize' }}>{val}</span>;
};

const ActionBtn = ({ label, color, hoverColor, onClick, disabled }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '5px 12px',
        border: 'none',
        borderRadius: '6px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontWeight: 600,
        fontSize: '11px',
        marginRight: '4px',
        marginBottom: '2px',
        background: disabled ? '#adb5bd' : (hovered ? hoverColor : color),
        color: 'white',
        opacity: disabled ? 0.7 : 1,
        transform: hovered && !disabled ? 'translateY(-1px)' : 'none',
        transition: 'all 0.2s',
      }}
    >{label}</button>
  );
};

const FilterBtn = ({ label, active, onClick }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '8px 16px',
        border: `2px solid ${active ? '#0171BB' : '#e9ecef'}`,
        borderRadius: '8px',
        background: active ? '#0171BB' : (hovered ? '#e6f4ff' : 'white'),
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '13px',
        color: active ? 'white' : (hovered ? '#0171BB' : '#495057'),
        borderColor: hovered && !active ? '#0171BB' : (active ? '#0171BB' : '#e9ecef'),
        transition: 'all 0.2s',
        whiteSpace: 'nowrap',
      }}
    >{label}</button>
  );
};

// Export chip. Sized to match FilterBtn so it sits cleanly at the end of each
// tab's filter bar. Disabled (and dimmed) when the current filters leave no
// rows to write.
const ExportBtn = ({ onClick, disabled, count }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={disabled ? 'Nothing to export for the current filters' : `Export ${count} row${count === 1 ? '' : 's'} to CSV`}
      style={{
        padding: '8px 16px',
        border: '2px solid #157347',
        borderRadius: '8px',
        background: disabled ? '#adb5bd' : (hovered ? '#157347' : '#198754'),
        borderColor: disabled ? '#adb5bd' : '#157347',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontWeight: 600,
        fontSize: '13px',
        color: 'white',
        opacity: disabled ? 0.7 : 1,
        transition: 'all 0.2s',
        whiteSpace: 'nowrap',
      }}
    >⬇ Export</button>
  );
};

// Reusable date-range toggle. Reuses FilterBtn so it visually matches the
// other filter chips. Each tab passes a `fieldOptions` registry; when more
// than one option is provided, a "by ⌄" dropdown renders so the user can
// pick which date column the range anchors on.
//
// When `mode === 'custom'`, two <input type="date"> fields render inline.
// Either bound may be left empty (open-ended). The parent owns the
// customFrom/customTo state and the onCustomChange callback.
const DateRangeToggle = ({
  mode, onChange,
  customFrom, customTo, onCustomChange,
  fieldOptions, field, onFieldChange,
}) => {
  const dateInputStyle = {
    padding: '6px 10px',
    border: '2px solid #e9ecef',
    borderRadius: '6px',
    fontSize: '13px',
    outline: 'none',
    color: '#495057',
    fontFamily: 'inherit',
  };
  const selectStyle = {
    ...dateInputStyle,
    cursor: 'pointer',
    background: 'white',
  };
  // Hide the dropdown when there's only one date column to pick (e.g.
  // Activity Log) — a one-option select would just be visual noise.
  const showFieldPicker = Array.isArray(fieldOptions) && fieldOptions.length > 1;
  return (
    <>
      <span style={{ fontWeight: 600, fontSize: '13px', color: '#495057', marginRight: '4px' }}>
        Date Range:
      </span>
      {DATE_RANGE_MODES.map((m) => (
        <FilterBtn
          key={m.id}
          label={m.label}
          active={mode === m.id}
          onClick={() => onChange(m.id)}
        />
      ))}
      {showFieldPicker && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginLeft: '4px' }}>
          <span style={{ fontSize: '13px', color: '#6b7280' }}>by</span>
          <select
            value={field}
            onChange={(e) => onFieldChange(e.target.value)}
            aria-label="Filter by date column"
            style={selectStyle}
          >
            {fieldOptions.map((f) => (
              <option key={f.id} value={f.id}>{f.label}</option>
            ))}
          </select>
        </span>
      )}
      {mode === 'custom' && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginLeft: '4px' }}>
          <input
            type="date"
            value={customFrom || ''}
            // Empty string means "no bound on this side" — store as null so
            // the predicate treats it as open-ended rather than the epoch.
            onChange={(e) => onCustomChange(e.target.value || null, customTo)}
            aria-label="From date"
            style={dateInputStyle}
          />
          <span style={{ fontSize: '13px', color: '#6b7280' }}>to</span>
          <input
            type="date"
            value={customTo || ''}
            onChange={(e) => onCustomChange(customFrom, e.target.value || null)}
            aria-label="To date"
            style={dateInputStyle}
          />
        </span>
      )}
    </>
  );
};

const Pagination = ({ page, totalPages, totalRecords, onPrev, onNext }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '16px',
    padding: '20px',
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    marginTop: '20px',
  }}>
    <PagBtn label="← Previous" disabled={page === 1} onClick={onPrev} />
    <span style={{ fontWeight: 600, color: '#495057', fontSize: '14px' }}>
      Page {page} of {totalPages} (Showing {Math.min(page * ROWS_PER_PAGE, totalRecords)} of {totalRecords} records – {ROWS_PER_PAGE} per page)
    </span>
    <PagBtn label="Next →" disabled={page === totalPages} onClick={onNext} />
  </div>
);

const PagBtn = ({ label, disabled, onClick }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '8px 16px',
        border: '2px solid #0171BB',
        borderRadius: '8px',
        background: hovered && !disabled ? '#0171BB' : 'white',
        color: hovered && !disabled ? 'white' : '#0171BB',
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.3 : 1,
        transition: 'all 0.2s',
      }}
    >{label}</button>
  );
};

// ─── Note Modal ───────────────────────────────────────────────────────────────

const NoteModal = ({ row, onClose, onSave }) => {
  const [note, setNote]           = useState('');
  const [saving, setSaving]       = useState(false);
  const [fetched, setFetched]     = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    if (!row) return;
    setLoading(true);
    fetch(`${NOTES_API}?action=get_notes&debt_id=${encodeURIComponent(row.debtId)}`)
      .then((r) => r.json())
      .then((data) => setFetched(Array.isArray(data) ? data : []))
      .catch(() => setFetched([]))
      .finally(() => setLoading(false));
  }, [row?.debtId]);

  if (!row) return null;

  // Merge API notes with any locally added ones (avoid duplicates by timestamp)
  const localNotes = row.notes || [];
  const fetchedKeys = new Set(fetched.map((n) => n.at || n.timestamp));
  const merged = [
    // Keep `type` so we can classify Posted Status vs Posted Note precisely
    // (isStatusEntry falls back to `by` when the live response omits `type`).
    ...fetched.map((n) => ({ text: n.note || n.text, by: n.noted_by || n.by, at: n.timestamp || n.at, type: n.type })),
    ...localNotes.filter((n) => !fetchedKeys.has(n.at)),
  ];

  const handleSave = async () => {
    if (!note.trim()) return;
    setSaving(true);
    const user = getLoggedInUser();
    const timestamp = new Date().toLocaleString();
    await fetch(CLAIM_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action:    'note',
        debt_id:   row.debtId,
        note:      note.trim(),
        noted_by:  user.name,
        timestamp,
      }),
    }).catch(() => {});
    onSave(row.id, { text: note.trim(), by: user.name, at: timestamp });
    setSaving(false);
    setNote('');
    onClose();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: '12px', width: '500px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)', padding: '24px',
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <div style={{ fontSize: '15px', fontWeight: 600, color: '#111827' }}>Notes</div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
              Debt ID: <strong>{row.debtId}</strong> &nbsp;·&nbsp; {row.currentCreditor}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#6b7280' }}>✕</button>
        </div>

        {/* Existing notes. System-generated events (claim/release/block
            auto-notes from the workflow) render with subdued italic styling
            so they don't compete with operator commentary. The workflow tags
            these by setting noted_by='[System]'. */}
        {loading ? (
          <div style={{ marginBottom: '16px', fontSize: '13px', color: '#9ca3af' }}>Loading notes...</div>
        ) : merged.length > 0 ? (
          <div style={{ marginBottom: '16px', maxHeight: '180px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {merged.map((n, i) => {
              // Status lines (Posted Status / system events) vs operator
              // commentary (Posted Note). Posted Notes get a distinct blue
              // tint so they stand out from the subdued gray status lines.
              const isSystem = isStatusEntry(n);
              return (
                <div key={i} style={{
                  background: isSystem ? '#f3f4f6' : '#fff7bd',
                  border:     isSystem ? '0.5px dashed #fff7bd' : '0.5px dashed #cfc788',
                  borderRadius: '8px',
                  padding: '10px 12px',
                }}>
                  <div style={{
                    fontSize: '13px',
                    color:     isSystem ? '#4b5563' : '#111827',
                    fontStyle: isSystem ? 'italic' : 'normal',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}>
                    {isSystem && <span style={{ fontSize: '10px', color: '#9ca3af' }} aria-hidden>⚙</span>}
                    {n.text}
                  </div>
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                    {isSystem ? 'System' : n.by} &nbsp;·&nbsp; {n.at}
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}

        <textarea
          autoFocus
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add a new note..."
          rows={3}
          style={{
            width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db',
            borderRadius: '8px', padding: '10px 12px', fontSize: '13px',
            resize: 'vertical', outline: 'none', fontFamily: 'inherit',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '12px' }}>
          <button onClick={onClose} style={{
            padding: '8px 16px', borderRadius: '8px', border: '1px solid #d1d5db',
            background: '#fff', cursor: 'pointer', fontSize: '13px', color: '#374151',
          }}>Cancel</button>
          <button onClick={handleSave} disabled={!note.trim() || saving} style={{
            padding: '8px 16px', borderRadius: '8px', border: 'none',
            background: !note.trim() || saving ? '#e5e7eb' : '#0171BB',
            color: !note.trim() || saving ? '#9ca3af' : '#fff',
            cursor: !note.trim() || saving ? 'not-allowed' : 'pointer',
            fontSize: '13px', fontWeight: 600,
          }}>{saving ? 'Saving...' : 'Save Note'}</button>
        </div>
      </div>
    </div>
  );
};

const BlockModal = ({ row, onClose, onBlock }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  // Deferred close: error paths show an AlertModal and only close this modal
  // once the user dismisses it (so the message isn't lost with the form).
  const [alertMsg, setAlertMsg] = useState(null);

  if (!row) return null;

  const handleBlock = async () => {
    if (!reason.trim()) return;
    setSaving(true);
    const user = getLoggedInUser();
    const timestamp = new Date().toLocaleString();
    try {
      const response = await fetch(CLAIM_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:     'block',
          debt_id:    row.debtId,
          reason:     reason.trim(),
          blocked_by: user.name,
          timestamp,
        }),
      });
      setSaving(false);
      if (response.status === 409) {
        // Another user holds the claim/block — refuse to overwrite. Surface
        // the conflict and bail out without moving the row to specialPop.
        const conflict = await response.json().catch(() => ({}));
        const message = conflict.error === 'claimed_by_other'
          ? `Cannot block — this row is currently claimed by ${conflict.by || 'another user'}.`
          : `This row was already blocked by ${conflict.by || 'another user'}${conflict.reason ? ` ("${conflict.reason}")` : ''}.`;
        setAlertMsg(message);
        return;
      }
      if (!response.ok) {
        setAlertMsg('Failed to block the row. Please try again.');
        return;
      }
      // Success — only NOW move the row to Special Populations.
      onBlock(row.id, { reason: reason.trim(), blockedBy: user.name, blockedAt: timestamp });
      onClose();
    } catch (err) {
      setSaving(false);
      console.error('Block network error', err);
      setAlertMsg('Network error blocking the row. Please try again.');
    }
  };

  return (
    <>
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: '12px', width: '480px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)', padding: '24px',
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <div style={{ fontSize: '15px', fontWeight: 600, color: '#dc3545' }}>Block Record</div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
              Debt ID: <strong>{row.debtId}</strong> &nbsp;·&nbsp; {row.currentCreditor}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#6b7280' }}>✕</button>
        </div>
        <textarea
          autoFocus
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Enter reason for blocking..."
          rows={4}
          style={{
            width: '100%', boxSizing: 'border-box', border: '1px solid #fca5a5',
            borderRadius: '8px', padding: '10px 12px', fontSize: '13px',
            resize: 'vertical', outline: 'none', fontFamily: 'inherit',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
          <button onClick={onClose} style={{
            padding: '8px 16px', borderRadius: '8px', border: '1px solid #d1d5db',
            background: '#fff', cursor: 'pointer', fontSize: '13px', color: '#374151',
          }}>Cancel</button>
          <button onClick={handleBlock} disabled={!reason.trim() || saving} style={{
            padding: '8px 16px', borderRadius: '8px', border: 'none',
            background: !reason.trim() || saving ? '#e5e7eb' : '#dc3545',
            color: !reason.trim() || saving ? '#9ca3af' : '#fff',
            cursor: !reason.trim() || saving ? 'not-allowed' : 'pointer',
            fontSize: '13px', fontWeight: 600,
          }}>{saving ? 'Blocking...' : 'Confirm Block'}</button>
        </div>
      </div>
    </div>
    <AlertModal message={alertMsg} onClose={() => { setAlertMsg(null); onClose(); }} />
    </>
  );
};

// ─── Release Confirmation Modal ───────────────────────────────────────────────
// Guards Release when the row is already marked in-progress by someone. Because
// anyone can free a stuck claim, releasing a row that another operator is
// actively processing would silently pull it out from under them. This modal
// surfaces WHO is processing (and since when) and forces an explicit
// Continue-vs-Cancel choice before the release goes through.
const ReleaseConfirmModal = ({ row, onClose, onConfirm }) => {
  if (!row) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: '12px', width: '460px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)', padding: '24px',
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <div>
            <div style={{ fontSize: '15px', fontWeight: 600, color: '#f59e0b' }}>⚠ Already In Progress</div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
              Debt ID: <strong>{row.debtId}</strong> &nbsp;·&nbsp; {row.currentCreditor}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#6b7280' }}>✕</button>
        </div>
        <div style={{ fontSize: '13px', color: '#374151', lineHeight: 1.5 }}>
          This record is currently being processed by{' '}
          <strong>{row.inProgressBy || 'another user'}</strong>
          {row.inProgressAt && <> (since {row.inProgressAt})</>}.
          Releasing it will clear the in-progress marker and free the claim.
          Do you want to continue?
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
          <button onClick={onClose} style={{
            padding: '8px 16px', borderRadius: '8px', border: '1px solid #d1d5db',
            background: '#fff', cursor: 'pointer', fontSize: '13px', color: '#374151',
          }}>Cancel</button>
          <button onClick={onConfirm} style={{
            padding: '8px 16px', borderRadius: '8px', border: 'none',
            background: '#f59e0b', color: '#fff', cursor: 'pointer',
            fontSize: '13px', fontWeight: 600,
          }}>Continue Release</button>
        </div>
      </div>
    </div>
  );
};

// ─── Alert Modal ──────────────────────────────────────────────────────────────
// In-app replacement for window.alert(), styled to match ReleaseConfirmModal so
// notifications feel native to the dashboard instead of a jarring browser popup.
// Purely informational — renders whatever message it's given and a single OK.
// Renders nothing when `message` is falsy, so a `useState(null)` toggle drives
// visibility the same way the other modals key off a row object.
const AlertModal = ({ message, onClose }) => {
  if (!message) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: '12px', width: '460px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)', padding: '24px',
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <div style={{ fontSize: '15px', fontWeight: 600, color: '#0171BB' }}>ℹ Notice</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#6b7280' }}>✕</button>
        </div>
        <div style={{ fontSize: '13px', color: '#374151', lineHeight: 1.5 }}>
          {message}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
          <button onClick={onClose} style={{
            padding: '8px 16px', borderRadius: '8px', border: 'none',
            background: '#0171BB', color: '#fff', cursor: 'pointer',
            fontSize: '13px', fontWeight: 600,
          }}>OK</button>
        </div>
      </div>
    </div>
  );
};

// ─── Loading Modal ────────────────────────────────────────────────────────────
// Shown on initial mount while the dashboard fetch is in flight. Hides as soon
// as the first loadData call settles (success OR failure) so the user sees the
// app even if the API errors. The Activity Log fetch is intentionally NOT
// gated on — it can take 30s+ and shouldn't block the main UI.
const LoadingModal = () => (
  <div style={{
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
  }}>
    <div style={{
      background: '#fff', borderRadius: '12px', padding: '24px 32px',
      boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      display: 'flex', alignItems: 'center', gap: '16px', minWidth: '280px',
    }}>
      <div style={{
        width: '24px', height: '24px',
        border: '3px solid #e5e7eb',
        borderTopColor: '#0171BB',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        flexShrink: 0,
      }} />
      <div>
        <div style={{ fontSize: '15px', fontWeight: 600, color: '#111827' }}>
          Loading settlement queue…
        </div>
        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
          Fetching dashboard data
        </div>
      </div>
    </div>
  </div>
);

// ─── Column Order (drag-to-reorder) ──────────────────────────────────────────
// Each table tab persists its column layout in localStorage so the user's
// preferred order survives reloads. Click headers to sort; drag headers to
// reorder — native HTML5 drag does NOT fire onClick after a real drag, so
// the two interactions coexist without disambiguation logic.
const COLUMN_ORDER_STORAGE_KEY = 'spq.columnOrder.v1';

const loadColumnOrder = (tabId) => {
  try {
    const all = JSON.parse(localStorage.getItem(COLUMN_ORDER_STORAGE_KEY) || '{}');
    return Array.isArray(all[tabId]) ? all[tabId] : null;
  } catch { return null; }
};

const saveColumnOrder = (tabId, order) => {
  try {
    const all = JSON.parse(localStorage.getItem(COLUMN_ORDER_STORAGE_KEY) || '{}');
    all[tabId] = order;
    localStorage.setItem(COLUMN_ORDER_STORAGE_KEY, JSON.stringify(all));
  } catch {}
};

// Reconciles a stored layout against the current column registry: drops keys
// that no longer exist, appends any newly-introduced keys at the end. So
// adding a column later doesn't reset a returning user's layout — the new
// column just appears rightmost until they move it.
const useColumnOrder = (tabId, defaultKeys) => {
  const [order, setOrder] = useState(() => {
    const stored = loadColumnOrder(tabId);
    if (!stored) return defaultKeys;
    const valid = stored.filter((k) => defaultKeys.includes(k));
    const missing = defaultKeys.filter((k) => !valid.includes(k));
    return [...valid, ...missing];
  });
  const update = (next) => {
    setOrder(next);
    saveColumnOrder(tabId, next);
  };
  const reset = () => {
    setOrder(defaultKeys);
    saveColumnOrder(tabId, defaultKeys);
  };
  return [order, update, reset];
};

// Bundles header-row drag/drop state and event handlers. Spread the return
// value onto each DraggableTH via the `drag` prop. onDragLeave is intentionally
// a no-op — leaving one TH almost always means entering the next, and
// clearing the drop-target indicator on leave would flicker visibly.
const useColumnDrag = (columnOrder, setColumnOrder) => {
  const [draggedKey, setDraggedKey] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);

  return {
    draggedKey,
    dragOverKey,
    onDragStart: (key) => (e) => {
      e.dataTransfer.effectAllowed = 'move';
      // Firefox requires setData for the drag to actually initiate.
      try { e.dataTransfer.setData('text/plain', key); } catch {}
      setDraggedKey(key);
    },
    onDragOver: (key) => (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (dragOverKey !== key) setDragOverKey(key);
    },
    onDragLeave: () => {},
    onDrop: (targetKey) => (e) => {
      e.preventDefault();
      if (!draggedKey || draggedKey === targetKey) {
        setDraggedKey(null); setDragOverKey(null); return;
      }
      const next = columnOrder.filter((k) => k !== draggedKey);
      const idx = next.indexOf(targetKey);
      if (idx === -1) { setDraggedKey(null); setDragOverKey(null); return; }
      next.splice(idx, 0, draggedKey);
      setColumnOrder(next);
      setDraggedKey(null);
      setDragOverKey(null);
    },
    onDragEnd: () => { setDraggedKey(null); setDragOverKey(null); },
  };
};

// ─── Claim / Release / Process Actions ───────────────────────────────────────
// Shared by Main Queue and Special Populations so both tabs hit the same
// workflow with identical optimistic-update, rollback, and 409-conflict
// handling. `setData` is the owning tab's row setter — each tab holds its own
// slice of the queue, so the hook never assumes which list a row lives in.
const useRowActions = (setData) => {
  // Row awaiting release confirmation because it's already in-progress. Set
  // when Release is clicked on such a row; cleared on cancel/confirm.
  const [releaseConfirmRow, setReleaseConfirmRow] = useState(null);
  // Message for the in-app AlertModal (replaces window.alert). Null = hidden.
  const [alertMsg, setAlertMsg] = useState(null);

  const handleClaim = async (row) => {
    const user = getLoggedInUser();
    const timestamp = new Date().toLocaleString();
    // Optimistic update — UI shows the claim immediately while the POST is in flight.
    setData((prev) =>
      prev.map((r) => r.id === row.id ? { ...r, claimedBy: user.name, claimedAt: timestamp } : r)
    );
    try {
      const response = await fetch(CLAIM_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:      'claim',
          debt_id:     row.debtId,
          claimed_by:  user.name,
          timestamp,
        }),
      });
      if (response.status === 409) {
        // Someone else already holds this claim. Read who, snap the UI to truth,
        // and let the user know.
        const conflict = await response.json().catch(() => ({}));
        const actualBy  = conflict.by  || 'another user';
        const actualAt  = conflict.at  || null;
        setData((prev) =>
          prev.map((r) => r.id === row.id
            ? { ...r, claimedBy: actualBy, claimedAt: actualAt }
            : r)
        );
        setAlertMsg(`This row was just claimed by ${actualBy}. Your claim was not recorded.`);
        return;
      }
      if (!response.ok) {
        // Other server error — roll back to unclaimed.
        setData((prev) =>
          prev.map((r) => r.id === row.id ? { ...r, claimedBy: null, claimedAt: null } : r)
        );
        console.error('Claim failed', response.status);
      }
    } catch (err) {
      // Network error — roll back so the user can retry.
      setData((prev) =>
        prev.map((r) => r.id === row.id ? { ...r, claimedBy: null, claimedAt: null } : r)
      );
      console.error('Claim network error', err);
    }
  };

  const handleRelease = async (row) => {
    const user = getLoggedInUser();
    const timestamp = new Date().toLocaleString();
    const previousClaimedBy = row.claimedBy;
    const previousClaimedAt = row.claimedAt;
    const previousInProgressBy = row.inProgressBy;
    const previousInProgressAt = row.inProgressAt;
    // Optimistic clear. Releasing also clears the in-progress marker so the
    // Process button becomes available again for whoever claims next.
    setData((prev) =>
      prev.map((r) => r.id === row.id
        ? { ...r, claimedBy: null, claimedAt: null, inProgressBy: null, inProgressAt: null }
        : r)
    );
    try {
      const response = await fetch(CLAIM_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'release',
          debt_id:      row.debtId,
          released_by:  user.name,
          timestamp,
        }),
      });
      if (response.status === 409) {
        // Stale-UI race: another user already released this row before our
        // click reached the server. The optimistic clear we applied happens
        // to match server state (the row IS unclaimed) — keep it cleared and
        // tell the user their action was a no-op so they aren't surprised
        // when the next refresh shows the row as available.
        setAlertMsg('This row was already released by someone else.');
        return;
      }
      if (!response.ok) {
        // Other server error — roll back so the row goes back to claimed
        // (and restore the in-progress marker cleared optimistically).
        setData((prev) =>
          prev.map((r) => r.id === row.id
            ? { ...r, claimedBy: previousClaimedBy, claimedAt: previousClaimedAt, inProgressBy: previousInProgressBy, inProgressAt: previousInProgressAt }
            : r)
        );
        console.error('Release failed', response.status);
      }
    } catch (err) {
      setData((prev) =>
        prev.map((r) => r.id === row.id
          ? { ...r, claimedBy: previousClaimedBy, claimedAt: previousClaimedAt, inProgressBy: previousInProgressBy, inProgressAt: previousInProgressAt }
          : r)
      );
      console.error('Release network error', err);
    }
  };

  // Entry point for the Release button. If the row is already in-progress
  // (someone clicked Process), don't release outright — open the confirmation
  // modal so the user knows they'd be pulling the record out from under whoever
  // is processing it. Otherwise release immediately.
  const requestRelease = (row) => {
    if (row.inProgressBy) {
      setReleaseConfirmRow(row);
      return;
    }
    handleRelease(row);
  };

  // Marks a row as processed. Mirrors handleClaim: optimistically records
  // WHO processed it and WHEN, and writes a local system event so the Note
  // modal reflects it immediately (the workflow writes the canonical event
  // server-side, reconciled on the next refresh). Tagged by='[System]' so
  // isSystemNote buckets it as an event, not operator commentary.
  const handleProcessed = async (row) => {
    const user = getLoggedInUser();
    const timestamp = new Date().toLocaleString();
    const event = { text: `In Progress by ${user.name}`, by: '[System]', at: timestamp };
    // Once marked, the row stays in-progress until it's released — the button
    // does not revert on its own (no rollback). Release is the only way back
    // to a processable state (see handleRelease, which clears inProgressBy).
    setData((prev) =>
      prev.map((r) => r.id === row.id
        ? { ...r, inProgressBy: user.name, inProgressAt: timestamp, notes: [...(r.notes || []), event] }
        : r)
    );
    try {
      const response = await fetch(CLAIM_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'process',
          debt_id:      row.debtId,
          in_progress_by: user.name,
          timestamp,
        }),
      });

      if (response.status === 409) {
        // Stale-UI race: another user already released this row before our
        // click reached the server. The optimistic clear we applied happens
        // to match server state (the row IS unclaimed) — keep it cleared and
        // tell the user their action was a no-op so they aren't surprised
        // when the next refresh shows the row as available.
        const conflict = await response.json().catch(() => ({}));
        const actualBy  = conflict.by  || 'another user';
        setAlertMsg(`This has been released by ${actualBy}. To process this settlement, please refresh the page for the latest status and re-claim.`);
        return;
      }
      if (!response.ok) {
        console.error('Processed failed', response.status);
      }
    } catch (err) {
      console.error('Processed network error', err);
    }
  };

  return {
    handleClaim, handleRelease, requestRelease, handleProcessed,
    releaseConfirmRow, setReleaseConfirmRow, alertMsg, setAlertMsg,
  };
};

// The two modals every tab with row actions needs: the "already in progress"
// release guard and the in-app alert. Rendered together so a tab only has to
// drop <RowActionModals actions={actions} /> at the end of its markup.
const RowActionModals = ({ actions }) => (
  <>
    <ReleaseConfirmModal
      row={actions.releaseConfirmRow}
      onClose={() => actions.setReleaseConfirmRow(null)}
      onConfirm={() => {
        const row = actions.releaseConfirmRow;
        actions.setReleaseConfirmRow(null);
        if (row) actions.handleRelease(row);
      }}
    />
    <AlertModal message={actions.alertMsg} onClose={() => actions.setAlertMsg(null)} />
  </>
);

// The Claim / Release / Process button group shared by both queue tabs.
// States (Note lives in its own column; Block is gated behind
// SHOW_BLOCK_ACTION and currently hidden):
//   - Unclaimed               → Claim ( + Block when enabled)
//   - Claimed by *me*         → Release + Process ( + Block when enabled)
//   - Claimed by someone else → Release + greyed Process (anyone can free a
//                               stuck claim; Block is claimer-only)
// Process is visible to EVERYONE so all users can see the row's state, but
// only ENABLED for the user who holds the claim. It's also disabled once
// in-progress (for everyone), where the label flips to "In-Process". Marking
// records an "In Progress by <user>" system event with a timestamp, same shape
// as the claim event (visible in the Note modal); releasing the row clears the
// marker so it becomes an enabled "Process" again for the claimer.
const RowActionButtons = ({ row, actions, currentUserName, onBlock }) => (
  <>
    {!row.claimedBy ? (
      <>
        <ActionBtn label="Claim" color="#0171BB" hoverColor="#015a94"
          onClick={() => actions.handleClaim(row)} />
        {SHOW_BLOCK_ACTION && (
          <ActionBtn label="Block" color="#dc3545" hoverColor="#b91c1c"
            onClick={() => onBlock(row)} />
        )}
      </>
    ) : (
      <>
        <ActionBtn label="Release" color="#f59e0b" hoverColor="#d97706"
          onClick={() => actions.requestRelease(row)} />
        {SHOW_BLOCK_ACTION && row.claimedBy === currentUserName && (
          <ActionBtn label="Block" color="#dc3545" hoverColor="#b91c1c"
            onClick={() => onBlock(row)} />
        )}
        <ActionBtn
          label={row.inProgressBy ? 'In-Process' : 'Process'}
          color="#28a745" hoverColor="#218838"
          disabled={!!row.inProgressBy || row.claimedBy !== currentUserName}
          onClick={() => actions.handleProcessed(row)}
        />
      </>
    )}
  </>
);

// "Claimed By" cell — shared so both tabs render the claim owner identically.
const ClaimedByCell = ({ row }) => row.claimedBy
  ? <div>
      <span style={{ color: '#0171BB', fontWeight: 500 }}>👤 {row.claimedBy}</span>
      {row.claimedAt && <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}>{row.claimedAt}</div>}
    </div>
  : <span style={{ color: '#adb5bd' }}>—</span>;

// Sortable + draggable header cell. Drag from any header onto another header
// to drop the dragged column before it. A plain click (no drag) still sorts.
const DraggableTH = ({
  colKey, label, sortable, sortActive, sortDir, onSortClick, drag, thBase,
}) => {
  const isDragged = drag.draggedKey === colKey;
  const isTarget  = drag.dragOverKey === colKey && drag.draggedKey && drag.draggedKey !== colKey;
  return (
    <th
      draggable
      onDragStart={drag.onDragStart(colKey)}
      onDragOver={drag.onDragOver(colKey)}
      onDragLeave={drag.onDragLeave}
      onDrop={drag.onDrop(colKey)}
      onDragEnd={drag.onDragEnd}
      onClick={() => sortable && onSortClick(colKey)}
      title="Drag to reorder · Click to sort"
      style={{
        ...thBase,
        cursor: sortable ? 'pointer' : 'grab',
        color: sortActive ? '#0171BB' : '#495057',
        opacity: isDragged ? 0.4 : 1,
        // 3px transparent border reserves the layout space so the table
        // doesn't shift horizontally when the drop-target indicator appears.
        borderLeft: isTarget ? '3px solid #0171BB' : '3px solid transparent',
        background: isTarget ? '#e6f4ff' : undefined,
        transition: 'background 0.15s, border-color 0.15s',
      }}
    >
      {label}{sortActive ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );
};

// ─── Tabs ─────────────────────────────────────────────────────────────────────

// Default order. Notes leads (per user-friendly request to keep notes on the
// left). Contact Name sits next to Client ID. Returning users with a saved
// layout in localStorage will see new columns ('notes', 'contactName')
// appended rightmost — they can drag them where they want.
const MAIN_QUEUE_COLUMN_KEYS = [
  'actions','claimedBy','notes', 
  'age', 'offerStatus', 'firstOfferPaymentDate',
  'clientId', 'contactName',
  'currentCreditor', 'originalCreditor', 'debtId',
  'epfTotal', 'epfCurrentMo', 'offerPaymentMethod', 'gateway', 'negotiator',
  'sifAuth', 'sifLetter', 
];

const MainQueueTab = ({ data, setData, setSpecialPop }) => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [dateRangeMode, setDateRangeMode] = useState('all');
  const [dateField, setDateField] = useState(MAIN_DATE_FIELDS[0].id);
  const [customFrom, setCustomFrom] = useState(null);
  const [customTo, setCustomTo] = useState(null);
  const [gatewayFilter, setGatewayFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [noteRow, setNoteRow] = useState(null);
  const [blockRow, setBlockRow] = useState(null);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  // Claim / Release / Process handlers + their modal state (see useRowActions).
  const actions = useRowActions(setData);

  // Identity for action-button gating. Release is open to everyone (anyone
  // can free a stuck claim) but Block is still gated to the user who holds
  // the claim — opening that up would let a non-claimer route someone
  // else's in-flight work to Special Populations without their knowledge.
  const currentUserName = getLoggedInUser().name;

  const [columnOrder, setColumnOrder] = useColumnOrder('main', MAIN_QUEUE_COLUMN_KEYS);
  const dragHandlers = useColumnDrag(columnOrder, setColumnOrder);

  const handleSort = (key) => {
    if (sortCol === key) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(key); setSortDir('asc'); }
    setPage(1);
  };

  const handleBlock = (rowId, blockInfo) => {
    let blocked;
    setData((prev) => {
      blocked = prev.find((r) => r.id === rowId);
      return prev.filter((r) => r.id !== rowId);
    });
    if (blocked) {
      setSpecialPop((prev) => [
        { ...blocked, blockReason: blockInfo.reason, blockedBy: blockInfo.blockedBy, blockedAt: blockInfo.blockedAt, offerStatus: 'Processing Issue' },
        ...prev,
      ]);
    }
  };

  const handleNoteSave = (rowId, newNote) => {
    setData((prev) =>
      prev.map((r) => r.id === rowId
        ? { ...r, notes: [...(r.notes || []), newNote] }
        : r)
    );
  };

  const filters = [
    { label: 'All',                              fn: () => true },
    { label: 'Ready for Full Processing',        fn: (r) => r.offerStatus === 'Ready Full' },
    { label: 'Ready for Partial Processing',     fn: (r) => r.offerStatus === 'Ready Partial' },
    { label: 'In Progress',                      fn: (r) => r.claimedBy != null },
    // dueWithin48hrs is computed client-side from FIRST_OFFER_PAYMENT_DATE
    // using Pacific calendar-day arithmetic (see daysUntilInPacific in the
    // normalizer). Matches a ±2-calendar-day window: 2 days past due through
    // 2 days from now, in Pacific. Records older than 2 days past due are
    // stale, not "urgent now".
    { label: 'First Payment Due ≤ 48 hrs',       fn: (r) => r.dueWithin48hrs },
    { label: 'Summons / Pre-Legal',              fn: (r) => r.summons || r.preLegal },
    { label: 'Current Mo Fee > $1,000',          fn: (r) => (r.epfCurrentMo || 0) > 1000 },
  ];

  const filterFn = filters.find((f) => f.label === activeFilter)?.fn || (() => true);
  // Date Range anchors on whatever column the user picked from the
  // "by ⌄" dropdown (First Offer Payment Date by default).
  const dateInRange = buildDateRangePredicate(dateRangeMode, customFrom, customTo);
  const dateGetter = (MAIN_DATE_FIELDS.find((f) => f.id === dateField) || MAIN_DATE_FIELDS[0]).get;

  // Distinct gateway chips are derived from the unfiltered `data` so the
  // chip set stays stable as other filters change (otherwise picking a
  // status filter would silently shrink the gateway options).
  const distinctGateways = Array.from(new Set(
    data.map((r) => r.gateway).filter((g) => g != null && g !== '')
  )).sort();

  const filtered = data.filter((r) => {
    const matchFilter  = filterFn(r);
    // Pull the date string via the chosen field's extractor — the value
    // is already a Pacific calendar day (per the API's emission convention).
    const matchDate    = dateInRange(dateGetter(r));
    const matchGateway = gatewayFilter === 'All' || r.gateway === gatewayFilter;
    const q = search.toLowerCase();
    const matchSearch = !search ||
      r.clientId.toLowerCase().includes(q) ||
      r.currentCreditor.toLowerCase().includes(q) ||
      r.originalCreditor.toLowerCase().includes(q) ||
      r.debtId.toLowerCase().includes(q);
    return matchFilter && matchDate && matchGateway && matchSearch;
  });

  const thBase = {
    padding: '14px 16px',
    textAlign: 'left',
    fontWeight: 600,
    fontSize: '12px',
    color: '#495057',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap',
    userSelect: 'none',
  };

  const td = {
    padding: '14px 16px',
    fontSize: '13px',
    whiteSpace: 'nowrap',
    color: '#1a1a1a',
  };

  // Extractors return null for missing values so the sorter can push them to
  // the bottom regardless of direction. Returning 0 / "" / "—" instead would
  // cluster placeholder rows at the top of an ascending sort.
  const isPlaceholder = (s) => !s || s === '—';

  // Per-column registry. Each entry pairs a header label, a sort extractor
  // (null = not sortable), and a cell renderer. Screen order is controlled
  // by `columnOrder` (drag-to-reorder), NOT by this object's key order.
  const columns = {
    // Notes lives as its own column (default leftmost) per stakeholder ask:
    // pulling notes out of the rightmost Actions cell makes the count visible
    // without scrolling and keeps it near each row's identifiers. The button
    // label splits manual notes from system events (see noteButtonLabel).
    actions: {
      label: 'Actions',
      sortKey: null,
      // Claim / Release / Process — see RowActionButtons for the state table.
      // Note lives in its own column (see `notes` below).
      render: (r) => (
        <RowActionButtons
          row={r} actions={actions} currentUserName={currentUserName}
          onBlock={setBlockRow}
        />
      ),
    },
    claimedBy: {
      label: 'Claimed By',
      sortKey: (r) => r.claimedBy || null,
      render: (r) => <ClaimedByCell row={r} />,
    },
    notes: {
      label: 'Notes',
      // Sort by total activity (manual notes + system events) so rows with
      // any history bubble together regardless of the split.
      sortKey: (r) => (r.serverNoteCount ?? 0) + (r.serverEventCount ?? 0) + (r.notes?.length ?? 0),
      render: (r) => (
        <ActionBtn
          label={noteButtonLabel(r)}
          color="#6c757d" hoverColor="#5a6268"
          onClick={() => setNoteRow(r)}
        />
      ),
    },
    age: {
      label: 'Age',
      sortKey: (r) => r.ageHours ?? (r.addedAt ? (Date.now() - new Date(r.addedAt).getTime()) / 3_600_000 : null),
      exportValue: (r) => csvText(r.ageLabel),
      render: (r) => <AgeBadge addedAt={r.addedAt} ageLabel={r.ageLabel} dueWithin48hrs={r.dueWithin48hrs} />,
    },
    offerStatus: {
      label: 'Offer Status',
      sortKey: (r) => r.offerStatus || null,
      exportValue: (r) => csvText(r.offerStatus),
      render: (r) => <StatusBadge status={r.offerStatus} />,
    },
    firstOfferPaymentDate: {
      label: 'First Offer Payment Date',
      sortKey: (r) => r.firstOfferPaymentDateRaw ? r.firstOfferPaymentDateRaw.getTime() : null,
      exportValue: (r) => csvText(r.firstOfferPaymentDate),
      render: (r) => r.firstOfferPaymentDate,
    },
    clientId: {
      label: 'Client ID',
      sortKey: (r) => r.clientId || null,
      exportValue: (r) => csvText(r.clientId),
      render: (r) => <ClientIdCell id={r.clientId} />,
    },
    // Contact Name is populated from C_FULLNAME by the API normalizer (see
    // normalizeApiRecord). Was previously captured but never rendered.
    contactName: {
      label: 'Contact Name',
      sortKey: (r) => r.clientName || null,
      exportValue: (r) => csvText(r.clientName),
      render: (r) => r.clientName || <span style={{ color: '#adb5bd' }}>—</span>,
    },
    currentCreditor: {
      label: 'Current Creditor',
      sortKey: (r) => isPlaceholder(r.currentCreditor) ? null : r.currentCreditor,
      exportValue: (r) => csvText(r.currentCreditor),
      render: (r) => r.currentCreditor,
    },
    originalCreditor: {
      label: 'Original Creditor',
      sortKey: (r) => isPlaceholder(r.originalCreditor) ? null : r.originalCreditor,
      exportValue: (r) => csvText(r.originalCreditor),
      render: (r) => r.originalCreditor,
    },
    debtId: {
      label: 'Debt ID',
      sortKey: (r) => r.debtId || null,
      exportValue: (r) => csvText(r.debtId),
      render: (r) => r.debtId,
    },
    epfTotal: {
      label: 'EPF Total / Settlement Fee',
      sortKey: (r) => r.epfTotal ?? null,
      exportValue: (r) => csvAmount(r.epfTotal),
      render: (r) => fmt(r.epfTotal),
    },
    epfCurrentMo: {
      label: 'EPF Current Mo',
      sortKey: (r) => r.epfCurrentMo ?? null,
      exportValue: (r) => csvAmount(r.epfCurrentMo),
      render: (r) => fmt(r.epfCurrentMo),
    },
    offerPaymentMethod: {
      label: 'Offer Payment Method',
      sortKey: (r) => r.offerPaymentMethod || null,
      exportValue: (r) => csvText(r.offerPaymentMethod),
      render: (r) => r.offerPaymentMethod,
    },
    // Gateway: GATEWAY_NAME from the workflow at eo50wwbfxqdmwof.
    // Renders "—" when null (missing or CRM placeholder).
    gateway: {
      label: 'Gateway',
      sortKey: (r) => r.gateway || null,
      exportValue: (r) => csvText(r.gateway),
      render: (r) => r.gateway || <span style={{ color: '#adb5bd' }}>—</span>,
    },
    negotiator: {
      label: 'Negotiator',
      sortKey: (r) => isPlaceholder(r.negotiator) ? null : r.negotiator,
      exportValue: (r) => csvText(r.negotiator),
      render: (r) => r.negotiator,
    },
    // SIF Auth / SIF Letter come back as status strings ("received", "pending",
    // "physical rcvd", etc.) — sort alphabetically with nulls at the bottom.
    sifAuth: {
      label: 'SIF Auth',
      sortKey: (r) => r.sifAuth || null,
      exportValue: (r) => csvBool(r.sifAuth),
      render: (r) => <BoolCell val={r.sifAuth} />,
    },
    sifLetter: {
      label: 'SIF Letter',
      sortKey: (r) => r.sifLetter || null,
      exportValue: (r) => csvBool(r.sifLetter),
      render: (r) => <BoolCell val={r.sifLetter} />,
    },
  };

  // Resolve the rendered column list from the saved order. Defensive filter
  // drops any stale key (e.g. from an older localStorage snapshot).
  const orderedColumns = columnOrder
    .map((k) => columns[k] ? { key: k, ...columns[k] } : null)
    .filter(Boolean);

  const sortable = sortCol && columns[sortCol]?.sortKey;
  const sorted = sortable
    ? [...filtered].sort((a, b) => {
        const av = columns[sortCol].sortKey(a);
        const bv = columns[sortCol].sortKey(b);
        // Nulls always last, regardless of direction.
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
        return sortDir === 'asc' ? cmp : -cmp;
      })
    : filtered;

  const totalPages = Math.max(1, Math.ceil(sorted.length / ROWS_PER_PAGE));
  const paged = sorted.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

  // Exports every filtered+sorted row (all pages), in the user's current
  // column order, minus the Actions / Claimed By / Notes columns.
  const handleExport = () => downloadCsv(orderedColumns, sorted, 'settlement_hub_main_queue');

  return (
    <div>
      {/* Filters Bar */}
      <div style={{
        background: 'white', padding: '16px 24px', borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '20px',
        display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center',
      }}>
        <span style={{ fontWeight: 600, fontSize: '14px', color: '#495057' }}>Filters:</span>
        {filters.map((f) => {
          const count = data.filter(f.fn).length;
          return (
            <FilterBtn
              key={f.label}
              label={`${f.label} (${count})`}
              active={activeFilter === f.label}
              onClick={() => { setActiveFilter(f.label); setPage(1); }}
            />
          );
        })}
        {/* Visual separator before the date-range group so it doesn't read
            as just another status filter. */}
        <span style={{ borderLeft: '1px solid #e5e7eb', height: '24px', margin: '0 4px' }} />
        <DateRangeToggle
          mode={dateRangeMode}
          onChange={(m) => { setDateRangeMode(m); setPage(1); }}
          customFrom={customFrom}
          customTo={customTo}
          onCustomChange={(f, t) => { setCustomFrom(f); setCustomTo(t); setPage(1); }}
          fieldOptions={MAIN_DATE_FIELDS}
          field={dateField}
          onFieldChange={(id) => { setDateField(id); setPage(1); }}
        />
        {/* Gateway filter. Chip set is built dynamically from distinct
            GATEWAY_NAME values in the loaded data — so the chips reflect
            whatever gateways the CRM actually has in flight, no maintenance
            needed if a new gateway is introduced. */}
        <span style={{ borderLeft: '1px solid #e5e7eb', height: '24px', margin: '0 4px' }} />
        <span style={{ fontWeight: 600, fontSize: '13px', color: '#495057', marginRight: '4px' }}>
          Gateway:
        </span>
        <FilterBtn
          label="All"
          active={gatewayFilter === 'All'}
          onClick={() => { setGatewayFilter('All'); setPage(1); }}
        />
        {distinctGateways.map((g) => (
          <FilterBtn
            key={g}
            label={g}
            active={gatewayFilter === g}
            onClick={() => { setGatewayFilter(g); setPage(1); }}
          />
        ))}
        <div style={{ flex: 1 }} />
        <input
          type="text"
          placeholder="Search by Client ID, Creditor..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          style={{
            minWidth: '260px', padding: '8px 16px',
            border: '2px solid #e9ecef', borderRadius: '8px',
            fontSize: '14px', outline: 'none',
          }}
          onFocus={(e) => (e.target.style.borderColor = '#0171BB')}
          onBlur={(e) => (e.target.style.borderColor = '#e9ecef')}
        />
        <ExportBtn onClick={handleExport} disabled={sorted.length === 0} count={sorted.length} />
      </div>

      {/* Table */}
      <div style={{
        background: 'white', borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflowX: 'auto',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1600px' }}>
          <thead style={{ background: '#f8f9fa', borderBottom: '2px solid #e9ecef' }}>
            <tr>
              {orderedColumns.map((col) => (
                <DraggableTH
                  key={col.key}
                  colKey={col.key}
                  label={col.label}
                  sortable={!!col.sortKey}
                  sortActive={sortCol === col.key}
                  sortDir={sortDir}
                  onSortClick={handleSort}
                  drag={dragHandlers}
                  thBase={thBase}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {paged.length === 0 ? (
              <tr>
                 <td colSpan={orderedColumns.length} style={{ ...td, textAlign: 'center', color: '#6c757d', padding: '32px' }}>
                  No records match the current filters.
                </td>
              </tr>
            ) : paged.map((row) => {
              // Claimed rows ARE the "In Progress" set (see filters: claimedBy
              // != null). Tint them so in-flight work stands out at a glance,
              // and keep the tint on mouse-leave rather than reverting to white.
              const isClaimed = row.claimedBy != null;
              const baseBg  = isClaimed ? '#e2fffd' : 'white';
              const hoverBg = isClaimed ? '#bbece8' : '#f8f9fa';
              return (
                <tr key={row.id} style={{
                  borderBottom: '1px solid #f1f3f5',
                  background: baseBg,
                  // Left accent bar reinforces the claimed state for anyone who
                  // has trouble distinguishing the subtle background tint.
                  boxShadow: isClaimed ? 'inset 3px 0 0 0 #0b6ab8' : 'none',
                }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = hoverBg)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = baseBg)}
                >
                  {orderedColumns.map((col) => (
                    <td key={col.key} style={td}>{col.render(row)}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page} totalPages={totalPages} totalRecords={sorted.length}
        onPrev={() => setPage((p) => p - 1)} onNext={() => setPage((p) => p + 1)}
      />
      <NoteModal row={noteRow} onClose={() => setNoteRow(null)} onSave={handleNoteSave} />
      {SHOW_BLOCK_ACTION && (
        <BlockModal row={blockRow} onClose={() => setBlockRow(null)} onBlock={handleBlock} />
      )}
      <RowActionModals actions={actions} />
    </div>
  );
};

// Toggle to show the Block action throughout the dashboard (Main Queue +
// Special Populations). Currently hidden — rows are server-managed and the
// CRM owns the "processing issue" lifecycle. Flip to true to re-enable
// the manual Block UI in both tabs.
const SHOW_BLOCK_ACTION = false;

// Toggle for the Special Populations "In Progress" filter chip. Hidden for now
// — Main Queue keeps its own In Progress chip. Flip to true to bring it back;
// the predicate below is left in place so nothing else needs changing.
const SHOW_SPECIAL_POP_IN_PROGRESS_FILTER = false;

// Default order mirrors Main Queue: notes leftmost, Contact Name next to
// Client ID. Returning users with a saved layout see new columns appended
// rightmost — they can drag them to where they want.
const SPECIAL_POP_COLUMN_KEYS = [
  //'actions','claimedBy',
  'notes',
  'age', 'offerStatus', 'firstOfferPaymentDate',
  'clientId', 'contactName',
  'currentCreditor', 'originalCreditor', 'debtId',
  'epfTotal', 'epfCurrentMo', 'offerPaymentMethod', 'gateway', 'negotiator',
  'sifAuth', 'sifLetter', 
];

const SpecialPopulationsTab = ({ data, setData }) => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [dateRangeMode, setDateRangeMode] = useState('all');
  const [dateField, setDateField] = useState(MAIN_DATE_FIELDS[0].id);
  const [customFrom, setCustomFrom] = useState(null);
  const [customTo, setCustomTo] = useState(null);
  const [gatewayFilter, setGatewayFilter] = useState('All');
  // Search is tab-local state, same as Main Queue's — the two tabs keep
  // independent query boxes rather than sharing one lifted value.
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [noteRow, setNoteRow] = useState(null);
  const [blockRow, setBlockRow] = useState(null);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  // Same Claim / Release / Process workflow as Main Queue — Special Populations
  // rows still need an owner while someone works the exception.
  const actions = useRowActions(setData);
  const currentUserName = getLoggedInUser().name;

  const [columnOrder, setColumnOrder] = useColumnOrder('special', SPECIAL_POP_COLUMN_KEYS);
  const dragHandlers = useColumnDrag(columnOrder, setColumnOrder);

  const handleSort = (key) => {
    if (sortCol === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(key); setSortDir('asc'); }
    setPage(1);
  };

  const handleNoteSave = (rowId, newNote) => {
    setData((prev) =>
      prev.map((r) => r.id === rowId
        ? { ...r, notes: [...(r.notes || []), newNote] }
        : r)
    );
  };

  const filters = [
    { label: 'All', fn: () => true },
    { label: 'Missing Letter Pays Submitted', fn: (r) => r.offerStatus === 'Missing Letter Pays Submitted' },
    { label: 'Processing Issue',              fn: (r) => r.offerStatus === 'Processing Issue' || r.blockReason != null },
    // Claimed rows ARE the in-progress set — same definition as Main Queue's
    // "In Progress" chip. Hidden behind the flag above.
    ...(SHOW_SPECIAL_POP_IN_PROGRESS_FILTER
      ? [{ label: 'In Progress',               fn: (r) => r.claimedBy != null }]
      : []),
  ];

  const filterFn = filters.find((f) => f.label === activeFilter)?.fn || (() => true);
  // Same date-field registry as Main Queue. The user picks the anchor
  // column from the toggle's "by ⌄" dropdown.
  const dateInRange = buildDateRangePredicate(dateRangeMode, customFrom, customTo);
  const dateGetter = (MAIN_DATE_FIELDS.find((f) => f.id === dateField) || MAIN_DATE_FIELDS[0]).get;

  // Distinct gateway chips built from unfiltered `data` so the chip set
  // stays stable as other filters change.
  const distinctGateways = Array.from(new Set(
    data.map((r) => r.gateway).filter((g) => g != null && g !== '')
  )).sort();

  // Same four searchable fields as Main Queue. All are normalizer-guaranteed
  // strings (see normalizeApiRecord), so the direct .toLowerCase() is safe.
  const q = search.toLowerCase();
  const matchSearch = (r) => !search ||
    r.clientId.toLowerCase().includes(q) ||
    r.currentCreditor.toLowerCase().includes(q) ||
    r.originalCreditor.toLowerCase().includes(q) ||
    r.debtId.toLowerCase().includes(q);

  const filtered = data.filter((r) =>
    filterFn(r) &&
    // Extract the date string via the user-selected field (default First
    // Offer Payment Date). String form keeps the filter aligned with what
    // the user sees in the chosen column.
    dateInRange(dateGetter(r)) &&
    (gatewayFilter === 'All' || r.gateway === gatewayFilter) &&
    matchSearch(r)
  );

  const isPlaceholder = (s) => !s || s === '—';

  const thBase = {
    padding: '14px 16px', textAlign: 'left', fontWeight: 600,
    fontSize: '12px', color: '#495057', textTransform: 'uppercase',
    letterSpacing: '0.5px', whiteSpace: 'nowrap', userSelect: 'none',
  };
  const td = { padding: '14px 16px', fontSize: '13px', whiteSpace: 'nowrap', color: '#1a1a1a' };

  // Per-column registry. The Actions column carries its own tdStyle override
  // because the block-reason callout needs minWidth and wrapping that the
  // default td style doesn't allow.
  const columns = {
    // Notes lives as its own column (default leftmost) per stakeholder ask:
    // the count is visible without scrolling to the rightmost Actions cell.
    // The button label splits manual notes from system events (see
    // noteButtonLabel) — same behavior as Main Queue's Notes column.
    claimedBy: {
      label: 'Claimed By',
      sortKey: (r) => r.claimedBy || null,
      render: (r) => <ClaimedByCell row={r} />,
    },
    notes: {
      label: 'Notes',
      // Sort by total activity (manual notes + system events).
      sortKey: (r) => (r.serverNoteCount ?? 0) + (r.serverEventCount ?? 0) + (r.notes?.length ?? 0),
      render: (r) => (
        <ActionBtn
          label={noteButtonLabel(r)}
          color="#6c757d" hoverColor="#5a6268"
          onClick={() => setNoteRow(r)}
        />
      ),
    },
    age: {
      label: 'Age',
      sortKey: (r) => r.ageHours ?? (r.addedAt ? (Date.now() - new Date(r.addedAt).getTime()) / 3_600_000 : null),
      exportValue: (r) => csvText(r.ageLabel),
      render: (r) => <AgeBadge addedAt={r.addedAt} ageLabel={r.ageLabel} dueWithin48hrs={r.dueWithin48hrs} />,
    },
    offerStatus: {
      label: 'Offer Status',
      sortKey: (r) => r.offerStatus || null,
      exportValue: (r) => csvText(r.offerStatus),
      render: (r) => <StatusBadge status={r.offerStatus} />,
    },
    firstOfferPaymentDate: {
      label: 'First Offer Payment Date',
      sortKey: (r) => r.firstOfferPaymentDateRaw ? r.firstOfferPaymentDateRaw.getTime() : null,
      exportValue: (r) => csvText(r.firstOfferPaymentDate),
      render: (r) => r.firstOfferPaymentDate,
    },
    clientId: {
      label: 'Client ID',
      sortKey: (r) => r.clientId || null,
      exportValue: (r) => csvText(r.clientId),
      render: (r) => <ClientIdCell id={r.clientId} />,
    },
    // Contact Name from C_FULLNAME (populated by normalizeApiRecord). Was
    // previously captured but not rendered anywhere.
    contactName: {
      label: 'Contact Name',
      sortKey: (r) => r.clientName || null,
      exportValue: (r) => csvText(r.clientName),
      render: (r) => r.clientName || <span style={{ color: '#adb5bd' }}>—</span>,
    },
    currentCreditor: {
      label: 'Current Creditor',
      sortKey: (r) => isPlaceholder(r.currentCreditor) ? null : r.currentCreditor,
      exportValue: (r) => csvText(r.currentCreditor),
      render: (r) => r.currentCreditor,
    },
    originalCreditor: {
      label: 'Original Creditor',
      sortKey: (r) => isPlaceholder(r.originalCreditor) ? null : r.originalCreditor,
      exportValue: (r) => csvText(r.originalCreditor),
      render: (r) => r.originalCreditor,
    },
    debtId: {
      label: 'Debt ID',
      sortKey: (r) => r.debtId || null,
      exportValue: (r) => csvText(r.debtId),
      render: (r) => r.debtId,
    },
    epfTotal: {
      label: 'EPF Total / Settlement Fee',
      sortKey: (r) => r.epfTotal ?? null,
      exportValue: (r) => csvAmount(r.epfTotal),
      render: (r) => fmt(r.epfTotal),
    },
    epfCurrentMo: {
      label: 'EPF Current Mo',
      sortKey: (r) => r.epfCurrentMo ?? null,
      exportValue: (r) => csvAmount(r.epfCurrentMo),
      render: (r) => fmt(r.epfCurrentMo),
    },
    offerPaymentMethod: {
      label: 'Offer Payment Method',
      sortKey: (r) => r.offerPaymentMethod || null,
      exportValue: (r) => csvText(r.offerPaymentMethod),
      render: (r) => r.offerPaymentMethod,
    },
    // Gateway: GATEWAY_NAME from the workflow at eo50wwbfxqdmwof
    // (same source as Main Queue's gateway column).
    gateway: {
      label: 'Gateway',
      sortKey: (r) => r.gateway || null,
      exportValue: (r) => csvText(r.gateway),
      render: (r) => r.gateway || <span style={{ color: '#adb5bd' }}>—</span>,
    },
    negotiator: {
      label: 'Negotiator',
      sortKey: (r) => isPlaceholder(r.negotiator) ? null : r.negotiator,
      exportValue: (r) => csvText(r.negotiator),
      render: (r) => r.negotiator,
    },
    sifAuth: {
      label: 'SIF Auth',
      sortKey: (r) => r.sifAuth || null,
      exportValue: (r) => csvBool(r.sifAuth),
      render: (r) => <BoolCell val={r.sifAuth} />,
    },
    sifLetter: {
      label: 'SIF Letter',
      sortKey: (r) => r.sifLetter || null,
      exportValue: (r) => csvBool(r.sifLetter),
      render: (r) => <BoolCell val={r.sifLetter} />,
    },
    actions: {
      label: 'Actions',
      sortKey: null,
      tdStyle: { ...td, minWidth: '160px', whiteSpace: 'normal', verticalAlign: 'top' },
      // Note button moved to its own leftmost column (see `notes` above).
      // Claim / Release / Process behave exactly as they do in Main Queue
      // (see RowActionButtons); below them sit the optional Block button
      // (hidden by default via SHOW_BLOCK_ACTION) and the block-reason
      // callout. Cell reserves ~260px so the callout has enough room to lay
      // out without breaking letters.
      render: (r) => (
        <>
          <RowActionButtons
            row={r} actions={actions} currentUserName={currentUserName}
            onBlock={setBlockRow}
          />
          {r.blockReason && (
            <div style={{
              // Block displays on its own line below the Note button.
              display: 'block',
              marginTop: '8px',
              background: '#fff7f7',
              border: '1px solid #fee2e2',
              borderLeft: '3px solid #f87171',
              borderRadius: '6px',
              padding: '6px 9px',
              maxWidth: '240px',
              whiteSpace: 'normal',
              overflowWrap: 'anywhere',
            }}>
              <div style={{
                fontSize: '11px',
                color: '#b91c1c',
                fontWeight: 500,
                lineHeight: 1.35,
                display: 'flex',
                gap: '4px',
              }}>
                <span style={{ flexShrink: 0 }}>⚠</span>
                <span>{r.blockReason}</span>
              </div>
              {(r.blockedBy || r.blockedAt) && (
                <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '4px', lineHeight: 1.3 }}>
                  {r.blockedBy && <span>{r.blockedBy}</span>}
                  {r.blockedBy && r.blockedAt && <span> · </span>}
                  {r.blockedAt && <span>{r.blockedAt}</span>}
                </div>
              )}
            </div>
          )}
        </>
      ),
    },
  };

  const orderedColumns = columnOrder
    .map((k) => columns[k] ? { key: k, ...columns[k] } : null)
    .filter(Boolean);

  const sortable = sortCol && columns[sortCol]?.sortKey;
  const sorted = sortable
    ? [...filtered].sort((a, b) => {
        const av = columns[sortCol].sortKey(a);
        const bv = columns[sortCol].sortKey(b);
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
        return sortDir === 'asc' ? cmp : -cmp;
      })
    : filtered;

  const totalPages = Math.max(1, Math.ceil(sorted.length / ROWS_PER_PAGE));
  const paged = sorted.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

  // Exports every filtered+sorted row (all pages), in the user's current
  // column order, minus the Actions / Claimed By / Notes columns.
  const handleExport = () => downloadCsv(orderedColumns, sorted, 'settlement_hub_special_populations');

  return (
    <div>
      <div style={{
        background: 'white', padding: '16px 24px', borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '20px',
        display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center',
      }}>
        <span style={{ fontWeight: 600, fontSize: '14px', color: '#495057' }}>Show:</span>
        {filters.map((f) => {
          const count = data.filter(f.fn).length;
          return (
            <FilterBtn
              key={f.label}
              label={`${f.label} (${count})`}
              active={activeFilter === f.label}
              onClick={() => { setActiveFilter(f.label); setPage(1); }}
            />
          );
        })}
        <span style={{ borderLeft: '1px solid #e5e7eb', height: '24px', margin: '0 4px' }} />
        <DateRangeToggle
          mode={dateRangeMode}
          onChange={(m) => { setDateRangeMode(m); setPage(1); }}
          customFrom={customFrom}
          customTo={customTo}
          onCustomChange={(f, t) => { setCustomFrom(f); setCustomTo(t); setPage(1); }}
          fieldOptions={MAIN_DATE_FIELDS}
          field={dateField}
          onFieldChange={(id) => { setDateField(id); setPage(1); }}
        />
        {/* Gateway filter — same dynamic-chip pattern as Main Queue. */}
        <span style={{ borderLeft: '1px solid #e5e7eb', height: '24px', margin: '0 4px' }} />
        <span style={{ fontWeight: 600, fontSize: '13px', color: '#495057', marginRight: '4px' }}>
          Gateway:
        </span>
        <FilterBtn
          label="All"
          active={gatewayFilter === 'All'}
          onClick={() => { setGatewayFilter('All'); setPage(1); }}
        />
        {distinctGateways.map((g) => (
          <FilterBtn
            key={g}
            label={g}
            active={gatewayFilter === g}
            onClick={() => { setGatewayFilter(g); setPage(1); }}
          />
        ))}
        <div style={{ flex: 1 }} />
        <input
          type="text"
          placeholder="Search by Client ID, Creditor..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          style={{
            minWidth: '260px', padding: '8px 16px',
            border: '2px solid #e9ecef', borderRadius: '8px',
            fontSize: '14px', outline: 'none',
          }}
          onFocus={(e) => (e.target.style.borderColor = '#0171BB')}
          onBlur={(e) => (e.target.style.borderColor = '#e9ecef')}
        />
        <ExportBtn onClick={handleExport} disabled={sorted.length === 0} count={sorted.length} />
      </div>

      <div style={{
        background: 'white', borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflowX: 'auto',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1400px' }}>
          <thead style={{ background: '#f8f9fa', borderBottom: '2px solid #e9ecef' }}>
            <tr>
              {orderedColumns.map((col) => (
                <DraggableTH
                  key={col.key}
                  colKey={col.key}
                  label={col.label}
                  sortable={!!col.sortKey}
                  sortActive={sortCol === col.key}
                  sortDir={sortDir}
                  onSortClick={handleSort}
                  drag={dragHandlers}
                  thBase={thBase}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {paged.length === 0 ? (
              <tr>
                <td colSpan={orderedColumns.length} style={{ ...td, textAlign: 'center', color: '#6c757d', padding: '32px' }}>
                  No records match the current filters.
                </td>
              </tr>
            ) : paged.map((row) => {
              // TEMPORARILY DISABLED — claimed-row highlighting (background
              // tint + blue left accent bar) is commented out for Special
              // Populations only; Main Queue keeps its highlighting. To
              // restore: uncomment the three consts and swap the <tr>
              // background / boxShadow / hover handlers back to them.
              //
              // Claimed rows are the ones someone is actively working. Tint
              // them so in-flight work stands out, and keep the tint on
              // mouse-leave rather than reverting to white (same as Main Queue).
              // const isClaimed = row.claimedBy != null;
              // const baseBg  = isClaimed ? '#e2fffd' : 'white';
              // const hoverBg = isClaimed ? '#bbece8' : '#f8f9fa';
              return (
                <tr key={row.id} style={{
                  borderBottom: '1px solid #f1f3f5',
                  background: 'white',
                  // Left accent bar reinforces the claimed state for anyone who
                  // has trouble distinguishing the subtle background tint.
                  // boxShadow: isClaimed ? 'inset 3px 0 0 0 #0b6ab8' : 'none',
                }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#f8f9fa')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'white')}
                >
                  {orderedColumns.map((col) => (
                    <td key={col.key} style={col.tdStyle || td}>{col.render(row)}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page} totalPages={totalPages} totalRecords={filtered.length}
        onPrev={() => setPage((p) => p - 1)} onNext={() => setPage((p) => p + 1)}
      />
      <NoteModal row={noteRow} onClose={() => setNoteRow(null)} onSave={handleNoteSave} />
      {SHOW_BLOCK_ACTION && (
        <BlockModal row={blockRow} onClose={() => setBlockRow(null)} onBlock={() => {}} />
      )}
      <RowActionModals actions={actions} />
    </div>
  );
};

// Toggle for the Activity Log's Export button. Hidden for now — Main Queue and
// Special Populations keep theirs. The export machinery (column exportValues +
// downloadCsv wiring) stays in place, so flipping this to true restores it.
const SHOW_ACTIVITY_LOG_EXPORT = false;

const ACTIVITY_LOG_COLUMN_KEYS = ['clientId', 'whoProcessed', 'settlementDate', 'cycleTime'];


const ActivityLogTab = ({ data }) => {
  const [page, setPage] = useState(1);
  const [dateRangeMode, setDateRangeMode] = useState('all');
  const [customFrom, setCustomFrom] = useState(null);
  const [customTo, setCustomTo] = useState(null);
  // Default to most-recently-completed first.
  const [sortCol, setSortCol] = useState('settlementDate');
  const [sortDir, setSortDir] = useState('desc');

  const [columnOrder, setColumnOrder] = useColumnOrder('activity', ACTIVITY_LOG_COLUMN_KEYS);
  const dragHandlers = useColumnDrag(columnOrder, setColumnOrder);

// Activity Log filters by Settlement Date — the date the cycle clock
  // stopped on each row. Pacific calendar dates, same as the other tabs.
  // Pass the date STRING (e.g. "2026-06-23"), not the parsed Date —
  // parsing midnight-Z timestamps shifts to the previous Pacific day.
  const dateInRange = buildDateRangePredicate(dateRangeMode, customFrom, customTo);
  const filtered = data.filter((r) => dateInRange(r.settlementDate));

  const thBase = {
    padding: '14px 16px', textAlign: 'left', fontWeight: 600,
    fontSize: '12px', color: '#495057', textTransform: 'uppercase',
    letterSpacing: '0.5px', whiteSpace: 'nowrap', userSelect: 'none',
  };
  const td = { padding: '14px 16px', fontSize: '13px', color: '#1a1a1a' };

  // Numeric sort keys (raw values, not display strings) so dates sort
  // chronologically and cycle time sorts by hours.
  const columns = {
    clientId:       { label: 'Client ID',       sortKey: (r) => r.clientId || '',
                      render: (r) => <ClientIdCell id={r.clientId} />,
                      exportValue: (r) => csvText(r.clientId) },
    whoProcessed:   { label: 'Who Processed',   sortKey: (r) => r.whoProcessed || '',
                      render: (r) => r.whoProcessed,
                      exportValue: (r) => csvText(r.whoProcessed) },
    settlementDate: { label: 'Settlement Date', sortKey: (r) => r.settlementDateRaw ? r.settlementDateRaw.getTime() : 0,
                      render: (r) => r.settlementDate,
                      exportValue: (r) => csvText(r.settlementDate) },
    cycleTime:      { label: 'Cycle Time',      sortKey: (r) => r.cycleHours ?? -1,
                      render: (r) => r.cycleTime,
                      exportValue: (r) => csvText(r.cycleTime) },
  };

  const orderedColumns = columnOrder
    .map((k) => columns[k] ? { key: k, ...columns[k] } : null)
    .filter(Boolean);

  const handleSort = (key) => {
    if (sortCol === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(key); setSortDir('asc'); }
    setPage(1);
  };

  const sortable = sortCol && columns[sortCol]?.sortKey;
  const sorted = sortable
    ? [...filtered].sort((a, b) => {
        const av = columns[sortCol].sortKey(a);
        const bv = columns[sortCol].sortKey(b);
        const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
        return sortDir === 'asc' ? cmp : -cmp;
      })
    : filtered;

  const totalPages = Math.max(1, Math.ceil(sorted.length / ROWS_PER_PAGE));
  const paged = sorted.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

  // Exports every filtered+sorted row (all pages), in the user's current
  // column order, minus the Actions / Claimed By / Notes columns.
  const handleExport = () => downloadCsv(orderedColumns, sorted, 'settlement_hub_activity_log');

  return (
    <div>
      {/* Filter bar — Activity Log only filters by date range. The toggle's
          field-picker dropdown auto-hides because Activity Log has just
          one date column (Settlement Date). */}
      <div style={{
        background: 'white', padding: '16px 24px', borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '20px',
        display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center',
      }}>
        <DateRangeToggle
          mode={dateRangeMode}
          onChange={(m) => { setDateRangeMode(m); setPage(1); }}
          customFrom={customFrom}
          customTo={customTo}
          onCustomChange={(f, t) => { setCustomFrom(f); setCustomTo(t); setPage(1); }}
          fieldOptions={ACTIVITY_DATE_FIELDS}
          field={ACTIVITY_DATE_FIELDS[0].id}
          onFieldChange={() => {}}
        />
        {SHOW_ACTIVITY_LOG_EXPORT && (
          <>
            <div style={{ flex: 1 }} />
            <ExportBtn onClick={handleExport} disabled={sorted.length === 0} count={sorted.length} />
          </>
        )}
      </div>
      
      <div style={{
        background: 'white', borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflowX: 'auto',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: '#f8f9fa', borderBottom: '2px solid #e9ecef' }}>
            <tr>
              {orderedColumns.map((col) => (
                <DraggableTH
                  key={col.key}
                  colKey={col.key}
                  label={col.label}
                  sortable={!!col.sortKey}
                  sortActive={sortCol === col.key}
                  sortDir={sortDir}
                  onSortClick={handleSort}
                  drag={dragHandlers}
                  thBase={thBase}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {paged.length === 0 ? (
              <tr>
                <td colSpan={orderedColumns.length} style={{ ...td, textAlign: 'center', color: '#6c757d', padding: '32px' }}>
                  {dateRangeMode === 'all'
                    ? 'No completed settlements yet.'
                    : 'No completed settlements in the selected date range.'}
                </td>
              </tr>
            ) : paged.map((row) => (
              <tr key={row.id} style={{ borderBottom: '1px solid #f1f3f5' }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#f8f9fa')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'white')}
              >
                {orderedColumns.map((col) => (
                  <td key={col.key} style={td}>{col.render(row)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page} totalPages={totalPages} totalRecords={sorted.length}
        onPrev={() => setPage((p) => p - 1)} onNext={() => setPage((p) => p + 1)}
      />
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

// Some endpoint fields arrive as formatted currency strings (e.g.
// "      $59,355.63" or "         $611.00") instead of numbers. Strip everything
// that isn't a digit, dot, or minus sign before parsing. Returns null on missing
// or unparseable input.
const parseCurrency = (v) => {
  if (v == null) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const n = Number(String(v).replace(/[^0-9.\-]/g, ''));
  return Number.isFinite(n) ? n : null;
};

// The endpoint wraps each summary metric as a one-element array of one-key objects:
//   summary.Count_of_Unprocessed === [{ SETTLEMENT_TOTAL_COUNT: "61" }]
//   summary.Current_Month_EPF_Total_Unprocessed === [{ ...: "      $59,355.63" }]
// Pulls the sole value out and parses through the currency cleaner so formatted
// strings work too.
const readSummaryNumber = (cell) => {
  if (!Array.isArray(cell) || !cell[0]) return null;
  return parseCurrency(Object.values(cell[0])[0]);
};

// ACTIVITY_LOG_API returns a flat array of completion events with pre-formatted
// CYCLE_TIME (e.g. "3d 7h 35m"). Total minutes for sorting are derived from the
// DAYS/HOURS/MINUTES split since the endpoint doesn't expose a single numeric
// field. Tolerant of older field names (END_CREATED_BY_NAME, CYCLE_TIME_READABLE,
// TOTAL_MINUTES) seen in earlier shapes of the workflow.
const normalizeActivityRecord = (r, idx) => {
  const endTime = r.END_TIME ? new Date(r.END_TIME) : null;
  const days    = Number(r.DAYS)    || 0;
  const hours   = Number(r.HOURS)   || 0;
  const minutes = Number(r.MINUTES) || 0;
  const totalMinutesFromSplit = days * 24 * 60 + hours * 60 + minutes;
  const totalMinutes = Number.isFinite(Number(r.TOTAL_MINUTES))
    ? Number(r.TOTAL_MINUTES)
    : (totalMinutesFromSplit > 0 ? totalMinutesFromSplit : null);
  return {
    id:                r.OFFER_ID || r.DEBT_ID || `${r.CONTACT_ID || 'row'}-${idx}`,
    clientId:          String(r.CONTACT_ID ?? ''),
    whoProcessed:      r.END_ACTOR_NAME || r.END_CREATED_BY_NAME || r.START_CREATED_BY_NAME || '—',
    // Match the date as the API returns it (date portion of the ISO timestamp).
    settlementDate:    r.END_TIME ? String(r.END_TIME).slice(0, 10) : '—',
    settlementDateRaw: endTime,
    cycleHours:        totalMinutes != null ? totalMinutes / 60 : null,
    cycleTime:         r.CYCLE_TIME || r.CYCLE_TIME_READABLE || '—',
  };
};


const SettlementProcessingQueue = () => {
  const [mainQueue, setMainQueue]         = useState([]);
  const [specialPop, setSpecialPop]       = useState([]);
  const [activityLog, setActivityLog]     = useState([]);
  const [summary, setSummary]             = useState(null);
  const [activeTab, setActiveTab]         = useState('main');
  const [lastUpdated, setLastUpdated]     = useState(null);
  const [refreshing, setRefreshing]       = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Cached identity used inside loadData's merge logic to distinguish "my
  // optimistic action" from "someone else's claim/block I happened to load".
  const currentUser = getLoggedInUser();

  // Keep a ref to current in-session state so the interval closure can read it
  const mainQueueRef  = useRef([]);
  const specialPopRef = useRef([]);
  useEffect(() => { mainQueueRef.current  = mainQueue; },  [mainQueue]);
  useEffect(() => { specialPopRef.current = specialPop; }, [specialPop]);

  const loadData = useCallback((isManual = false) => {
    if (isManual) setRefreshing(true);
    Promise.all([
      fetch(MAIN_API_URL).then((r) => r.json()),
      // Side-car: server-side claim/block/note state from the action workflow's
      // data stores. If the workflow isn't deployed (or get_state isn't implemented
      // yet), this resolves to {} and the UI falls back to session-only state.
      fetch(`${CLAIM_API}?action=get_state`)
        .then((r) => {
          if (!r.ok) {
            console.warn('get_state returned', r.status, '— claim/block state may be stale');
            return {};
          }
          return r.json();
        })
        .catch((err) => {
          console.warn('get_state network error — claim/block state may be stale', err);
          return {};
        }),
    ])
      .then(([data, state]) => {
        // The endpoint returns { summary, dashboard_rows, dashboard_row_count }.
        // Older shape was a bare array; tolerate both so the UI doesn't break
        // mid-deploy if the API rolls back.
        const rows = Array.isArray(data)
          ? data
          : Array.isArray(data?.dashboard_rows) ? data.dashboard_rows : [];
        if (!rows.length && !Array.isArray(data) && !data?.dashboard_rows) {
          console.warn('Settlement queue API returned unexpected shape', data);
        }
        setSummary(data?.summary || null);

        // Expected side-car shape (defensive — any missing key collapses to {}):
        //   { claims:      { [debtId]: { claimed_by, claimed_at } },
        //     blocks:      { [debtId]: { reason, blocked_by, blocked_at } },
        //     noteCounts:  { [debtId]: number },   // manual operator notes
        //     eventCounts: { [debtId]: number } }  // system auto-notes (claim/release/block)
        // When the workflow hasn't shipped eventCounts yet, the map stays
        // empty and the Note button label collapses to its single-count form.
        const claimsMap   = state?.claims      || {};
        const blocksMap   = state?.blocks      || {};
        const noteCounts  = state?.noteCounts  || {};
        const eventCounts = state?.eventCounts || {};
        // Per-row in-progress marker written by the action workflow, keyed by
        // debtId: { in_progress_by, in_progress_at }. Used as the baseline for
        // the Process button on (re)load, then refined against the note timeline
        // below (which is authoritative for release-supersedes semantics).
        const processMap  = state?.process     || {};

        const records = rows.map((row) => {
          const base  = normalizeApiRecord(row);
          const claim = claimsMap[base.debtId];
          const block = blocksMap[base.debtId];
          const proc  = processMap[base.debtId];
          // Baseline in-progress from get_state's `process` map so the Process
          // button reflects server state immediately on (re)load. Refined
          // against the per-row note timeline in the reconcile step below,
          // which is authoritative — the latest "Posted Status" note correctly
          // reflects a release that supersedes an earlier "In Progress by".
          return {
            ...base,
            claimedBy:   claim?.claimed_by || base.claimedBy,
            claimedAt:   claim?.claimed_at || base.claimedAt,
            inProgressBy: proc?.in_progress_by || null,
            inProgressAt: proc?.in_progress_at || null,
            blockReason: block?.reason     || base.blockReason,
            blockedBy:   block?.blocked_by || null,
            blockedAt:   block?.blocked_at || null,
            // A server-side block routes the row to Special Populations regardless
            // of what CRM says about OFFER_STATUS.
            offerStatus: block?.reason ? 'Processing Issue' : base.offerStatus,
            serverNoteCount:  noteCounts[base.debtId]  ?? null,
            serverEventCount: eventCounts[base.debtId] ?? null,
          };
        });

        // Merge in-session overrides (claim/block/notes) into freshly fetched records.
        // In-session overrides are keyed by debtId and take precedence over CRM data
        // for fields the hub manages (claimedBy, claimedAt, notes, offerStatus when blocked).
        const sessionOverrides = {};
        [...mainQueueRef.current, ...specialPopRef.current].forEach((r) => {
          if (r.debtId) sessionOverrides[r.debtId] = r;
        });

        const merged = records.map((r) => {
          const override = sessionOverrides[r.debtId];
          if (!override) return r;
          // Honor session overrides ONLY for actions the local user took. For
          // claims/blocks held by someone else, the server's get_state is the
          // source of truth — using `??` here meant a previous refresh's
          // claimedBy="Karen" would persist even after Karen released the row,
          // because the override never got cleared.
          const ownClaim = override.claimedBy && override.claimedBy === currentUser.name;
          const ownBlock = override.blockedBy && override.blockedBy === currentUser.name;
          // Local user's optimistic Process — keep the marker until the
          // server's events reflect it, so the button doesn't flicker back to
          // "Process" between clicking and the next refresh. Cleared locally on
          // release (handleRelease nulls inProgressBy), so this won't resurrect
          // a released row.
          const ownInProgress = override.inProgressBy && override.inProgressBy === currentUser.name;
          return {
            ...r,
            // Local user's optimistic claim — keep until server confirms.
            claimedBy: ownClaim ? override.claimedBy : r.claimedBy,
            claimedAt: ownClaim ? override.claimedAt : r.claimedAt,
            inProgressBy: ownInProgress ? override.inProgressBy : r.inProgressBy,
            inProgressAt: ownInProgress ? override.inProgressAt : r.inProgressAt,
            // Local notes are cleared on refresh — server's serverNoteCount is
            // authoritative. Keeping override.notes here would double-count
            // (server count would include the just-added note AND the local
            // array would still have it).
            notes: undefined,
            // Local user's optimistic block — keep until server confirms.
            offerStatus: (ownBlock && override.offerStatus === 'Processing Issue')
              ? 'Processing Issue'
              : r.offerStatus,
            blockReason: ownBlock ? override.blockReason : r.blockReason,
          };
        });

        const active = merged.filter((r) => !COMPLETED_STATUSES.has(r.offerStatus));
        const mainRows    = active.filter((r) => !SPECIAL_STATUSES.has(r.offerStatus));
        const specialRows = active.filter((r) => SPECIAL_STATUSES.has(r.offerStatus));
        setMainQueue(mainRows);
        setSpecialPop(specialRows);
        // Activity Log is loaded separately via loadActivityLog() — see below.
        setLastUpdated(new Date());

        // Reconcile the Process-button state against the authoritative note
        // timeline. get_state exposes the current `process` marker (the baseline
        // set above) but not the note history, so we fetch get_notes for the
        // claimed rows — the only ones that can be in-progress — and flip
        // inProgressBy based on the LATEST "Posted Status" entry. A trailing
        // "Released by" status supersedes an earlier "In Progress by" (button
        // re-enables); a plain "Posted Note" written afterward leaves it intact.
        // Runs after the tables are shown so a slow notes fetch never blocks the
        // initial render. Applied to BOTH tabs — Special Populations carries the
        // same Claim/Release/Process buttons, so it needs the same reconcile.
        const reconcileInProgress = (rows, setRows) => {
          const claimed = rows.filter((r) => r.claimedBy && r.debtId);
          if (!claimed.length) return;
          Promise.all(claimed.map((r) =>
            fetch(`${NOTES_API}?action=get_notes&debt_id=${encodeURIComponent(r.debtId)}`)
              .then((res) => (res.ok ? res.json() : []))
              .catch(() => [])
              .then((notes) => {
                const arr = Array.isArray(notes) ? notes : [];
                return {
                  id: r.id,
                  inProgress: deriveInProgressFromLatestNote(arr),
                  released:   isLatestStatusReleased(arr),
                };
              })
          )).then((results) => {
            const byId = {};
            results.forEach((res) => { byId[res.id] = res; });
            setRows((prev) => prev.map((r) => {
              if (!(r.id in byId)) return r;
              const { inProgress, released } = byId[r.id];
              if (inProgress?.by) return { ...r, inProgressBy: inProgress.by, inProgressAt: inProgress.at || null };
              // Latest status is an explicit "Released by" → the row is free.
              // Clear BOTH the in-progress marker AND the claim, overriding any
              // local optimistic state. This is what forces the CLAIMER's view
              // back to the "Claim" button when SOMEONE ELSE released their row:
              // the merge re-applies the claimer's own optimistic claim every
              // refresh (ownClaim), so without clearing it here the releasee
              // would never see the release. Safe against a legit re-claim: if
              // the current user actually holds the claim, the latest status
              // note is "Claimed by <user>" (not "Released by"), so this branch
              // doesn't fire. A get_state claim/note that lags a hair behind an
              // optimistic re-claim self-heals on the next refresh.
              if (released) return { ...r, inProgressBy: null, inProgressAt: null, claimedBy: null, claimedAt: null };
              // No active in-progress in the notes. Preserve a just-set optimistic
              // marker by the current user (the server may not have recorded the
              // note yet — see handleProcessed); otherwise clear it.
              if (r.inProgressBy && r.inProgressBy === currentUser.name) return r;
              return { ...r, inProgressBy: null, inProgressAt: null };
            }));
          });
        };
        reconcileInProgress(mainRows, setMainQueue);
        reconcileInProgress(specialRows, setSpecialPop);
      })
      .catch((err) => { console.error('Settlement queue load failed', err); })
      .finally(() => {
        if (isManual) setRefreshing(false);
        // Dismiss the initial-mount modal whether the load succeeded or failed —
        // a permanent modal on a broken backend is worse than a visibly-empty UI.
        setInitialLoading(false);
      });
  }, []);


  // Activity Log lives on its own endpoint and pulls a much larger payload
  // (~6MB+). Decoupled from the 3-min auto-refresh so dashboard responsiveness
  // is unaffected when the activity-log query is slow or transiently failing.
  const loadActivityLog = useCallback(() => {
    fetch(ACTIVITY_LOG_API)
      .then((r) => (r.ok ? r.json() : []))
      .catch(() => [])
      .then((rows) => {
        if (!Array.isArray(rows)) {
          console.warn('Activity log API returned unexpected shape', rows);
          return;
        }
        setActivityLog(rows.map(normalizeActivityRecord));
      })
      .catch((err) => { console.error('Activity log load failed', err); });
  }, []);

  useEffect(() => {
    loadData();
    loadActivityLog();
    const interval = setInterval(() => loadData(), REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadData, loadActivityLog]);

  // Refresh whenever the user comes back to the tab — closes the staleness
  // gap that opens when someone leaves the page open for a while. Without
  // this, two users could each see the same "available" row on screens that
  // haven't refreshed since their last interaction.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') loadData();
    };
    const onFocus = () => loadData();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [loadData]);

  // ── Derived metrics ──
  const isReady = (r) =>
    r.offerStatus === 'Ready Full' ||
    r.offerStatus === 'Ready Partial' ||
    r.offerStatus === 'Ready For Processing';

  // Prefer server-side aggregates when present — `dashboard_rows` only contains
  // unprocessed items, and `CURRENT_MONTH_EPF_SUM` is rarely populated per row,
  // so client-side sums under-report.
  const summaryUnprocessed = readSummaryNumber(summary?.Count_of_Unprocessed);
  const summaryEpfTotal    = readSummaryNumber(summary?.Current_Month_EPF_Total_Unprocessed);
  const summaryIssueCount  = readSummaryNumber(summary?.Count_of_Settlements_Processing_Issue);

  // The first three tiles follow the selected tab: on Main Queue they describe
  // the unprocessed queue, on Special Populations the exception set. Activity
  // Log has no comparable rows (different record shape — cycle times, not ages),
  // so it keeps showing the Main Queue figures.
  //
  // The server aggregates above summarize ONLY the unprocessed / Main Queue
  // population, so they're consulted on Main Queue (and Activity Log) but never
  // on Special Populations — that tab always sums its own rows, otherwise
  // selecting it would display Main Queue's server totals.
  const metricRows = activeTab === 'special' ? specialPop : mainQueue;
  const useServerSummary = activeTab !== 'special';

  const unprocessedCount = (useServerSummary ? summaryUnprocessed : null) ?? metricRows.length;

  const epfCurrentMoTotal = (useServerSummary ? summaryEpfTotal : null) ?? metricRows
    .reduce((sum, r) => sum + (r.epfCurrentMo || 0), 0);

  const avgAgeHours = (() => {
    const withAge = metricRows.filter((r) => r.ageHours != null || r.addedAt);
    if (!withAge.length) return 0;
    return withAge.reduce((sum, r) => {
      if (r.ageHours != null) return sum + r.ageHours;
      return sum + (Date.now() - new Date(r.addedAt).getTime()) / (1000 * 60 * 60);
    }, 0) / withAge.length;
  })();

  // "Processing Issue / Block Count" reflects ONLY Processing Issue rows
  // (Missing Letter rows route to the same tab but don't belong here).
  // Combines the server's summary with locally-blocked rows so the card
  // increments immediately when a user clicks Block on a Main Queue or
  // Special Populations row — without waiting for the next server refresh
  // to acknowledge the action. Math.max avoids double-counting: server
  // returned rows are already in specialPop, so the local tally meets or
  // exceeds the server count once the action workflow rolls them in.
  const localIssueCount = specialPop
    .filter((r) => r.offerStatus === 'Processing Issue').length;
  const issueCount = Math.max(summaryIssueCount ?? 0, localIssueCount);

  const formatEPF = (n) => {
    if (n >= 1000) return `$${(n / 1000).toFixed(1)}K`;
    return `$${n.toLocaleString()}`;
  };

  const formatAvgAge = (h) => {
    if (h >= 24) return `${(h / 24).toFixed(1)}d`;
    return `${h.toFixed(1)}h`;
  };

  // ── Tab config ──
  const tabs = [
    { id: 'main',     label: 'Main Queue',            count: mainQueue.length,  badgeStyle: { background: '#0171BB', color: 'white' } },
    { id: 'special',  label: 'Special Populations',   count: specialPop.length, badgeStyle: { background: '#ffc107', color: '#000' } },
    { id: 'activity', label: 'Activity Log',           count: activityLog.length,badgeStyle: { background: '#28a745', color: 'white' } },
  ];

  // Labels stay fixed; the subtitles name the set each value was computed from,
  // so the tiles stay truthful when the tab swaps the source rows out from
  // under them. Activity Log shows the Main Queue figures (see metricRows), so
  // it keeps the Main Queue wording. The Processing Issue tile is
  // tab-independent — it always tallies the same exception set.
  const onSpecial = activeTab === 'special';

  const metricCards = [
    { label: 'Count of Unprocessed',     value: unprocessedCount,             subtitle: onSpecial ? 'Special Populations rows' : 'Ready settlements'    },
    { label: 'Current Mo EPF Total',     value: formatEPF(epfCurrentMoTotal), subtitle: onSpecial ? 'Special Populations EPF'  : 'Unprocessed EPF'      },
    { label: 'Average Age',              value: formatAvgAge(avgAgeHours),    subtitle: onSpecial ? 'Of Special Populations'   : 'Of unprocessed items' },
    { label: 'Processing Issue',         value: issueCount,                   subtitle: 'Requiring attention'  },
  ];

  const tabLabels = {
    main: 'Main Queue',
    special: 'Special Populations',
    activity: 'Activity Log',
  };

  return (
    <StyledContainer>
      {initialLoading && <LoadingModal />}
      <div className="app-container">
        <div className="container">

          {/* Page Title + Breadcrumbs */}
          <h1>Settlements Processing Hub</h1>
          <Breadcrumbs />

          {/* Metric Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            margin: '20px 0',
          }}>
            {metricCards.map((m) => (
              <div key={m.label} style={{
                background: 'white', padding: '20px', borderRadius: '8px',
                border: '1px solid #e5e7eb',
              }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>
                  {m.label}
                </div>
                <div style={{ fontSize: '32px', fontWeight: 700, color: '#2563eb', marginBottom: '4px' }}>
                  {m.value}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>{m.subtitle}</div>
              </div>
            ))}
          </div>

          {/* Last Updated + Refresh */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <span style={{ fontSize: '12px', color: '#9ca3af' }}>
              {lastUpdated
                ? `Last updated: ${lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`
                : 'Loading…'}
            </span>
            <button
              onClick={() => { loadData(true); loadActivityLog(); }}
              disabled={refreshing}
              style={{
                display: 'flex', alignItems: 'center', gap: '5px',
                padding: '5px 12px', fontSize: '12px', fontWeight: 600,
                border: '1px solid #d1d5db', borderRadius: '6px',
                background: refreshing ? '#f3f4f6' : '#fff',
                color: refreshing ? '#9ca3af' : '#374151',
                cursor: refreshing ? 'not-allowed' : 'pointer',
              }}
            >
              <span style={{ display: 'inline-block', animation: refreshing ? 'spin 1s linear infinite' : 'none' }}>↻</span>
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>

          {/* Tab Navigation */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px 8px 0 0',
            border: '1px solid #e5e7eb',
            borderBottom: 'none',
            display: 'flex',
          }}>
            {tabs.map((tab, i) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  border: 'none',
                  borderRadius: i === 0 ? '8px 0 0 0' : i === tabs.length - 1 ? '0 8px 0 0' : '0',
                  cursor: 'pointer',
                  backgroundColor: activeTab === tab.id ? '#2563eb' : '#f9fafb',
                  color: activeTab === tab.id ? 'white' : '#6b7280',
                  borderRight: i < tabs.length - 1 ? '1px solid #e5e7eb' : 'none',
                  transition: 'all 0.2s ease',
                }}
              >
                {tab.label}
                <span style={{
                  marginLeft: '8px',
                  fontSize: '12px',
                  padding: '2px 6px',
                  borderRadius: '12px',
                  backgroundColor: activeTab === tab.id ? 'rgba(255,255,255,0.2)' : tab.badgeStyle.background,
                  color: activeTab === tab.id ? 'white' : tab.badgeStyle.color,
                  fontWeight: 700,
                }}>{tab.count}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0 0 8px 8px',
            border: '1px solid #e5e7eb',
            padding: '16px',
            minHeight: '600px',
          }}>
            {activeTab === 'main'     && <MainQueueTab data={mainQueue} setData={setMainQueue} setSpecialPop={setSpecialPop} />}
            {activeTab === 'special'  && <SpecialPopulationsTab data={specialPop} setData={setSpecialPop} />}
            {activeTab === 'activity' && <ActivityLogTab data={activityLog} />}
          </div>

          {/* Status Bar */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            padding: '12px 16px',
            marginTop: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '12px',
            color: '#6b7280',
          }}>
            <div>
              <strong>Data Status:</strong>
              <span style={{ color: '#059669', marginLeft: '8px' }}>
                {mainQueue.length} records loaded
              </span>
            </div>
            <div>
              <strong>Active Tab:</strong>
              <span style={{
                marginLeft: '8px', padding: '2px 8px',
                backgroundColor: '#dbeafe', borderRadius: '4px', fontWeight: '500',
              }}>
                {tabLabels[activeTab]}
              </span>
            </div>
          </div>

          <div className='custom-footer'>
            <Footer/>
          </div>

        </div>
      </div>
    </StyledContainer>
  );
};

export default SettlementProcessingQueue;

const StyledContainer = styled.div`
  .spanText {
    padding-right: 10px;
    font-size: 20px;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
`;
